<template>
  <div>
    <section class="section is-main-section">
      <!-- Storage Credentials Section -->
      <CardComponent>
        <div class="section">
          <h1 class="title">Storage Credentials</h1>
          <p class="subtitle">
            Manage cloud storage credentials for S3, Wasabi, Google Cloud, and local/NAS storage
          </p>

          <div class="has-text-right">
            <b-button
              type="is-primary"
              icon-left="plus"
              @click="openCreateCredentialModal"
            >
              Add Storage Credential
            </b-button>
          </div>

          <b-table
            :data="storageCredentials"
            :bordered="false"
            :striped="true"
            :narrowed="true"
            :hoverable="true"
            :loading="loadingCredentials"
            :focusable="false"
            :mobile-cards="true"
            class="has-margin-top-15"
          >
            <template slot-scope="props">
              <b-table-column field="storage_name" label="Name">
                {{ props.row.storage_name }}
              </b-table-column>

              <b-table-column field="storage_provider" label="Provider">
                <b-tag :type="getProviderTagType(props.row.storage_provider)">
                  {{ getProviderLabel(props.row.storage_provider) }}
                </b-tag>
              </b-table-column>

              <b-table-column field="bucket_name" label="Bucket/Path">
                <span v-if="['s3', 's3_compatible', 'huawei_obs', 'wasabi', 'google', 'gcp', 'azure'].includes(props.row.storage_provider)">
                  {{ props.row.bucket_name }}
                </span>
                <span v-else-if="['smb', 'nfs'].includes(props.row.storage_provider)">
                  {{ props.row.network_path }}
                </span>
                <span v-else-if="props.row.storage_provider === 'sftp'">
                  {{ props.row.ftp_host }}{{ props.row.base_path ? ':' + props.row.base_path : '' }}
                </span>
                <span v-else>
                  {{ props.row.base_path }}
                </span>
              </b-table-column>

              <b-table-column field="region" label="Region">
                {{ props.row.region || '-' }}
              </b-table-column>

              <b-table-column field="is_public" label="Access">
                <b-tag
                  :type="props.row.is_public ? 'is-success' : 'is-warning'"
                >
                  {{ props.row.is_public ? 'Public' : 'Private' }}
                </b-tag>
              </b-table-column>

              <b-table-column class="has-text-centered" label="Actions">
                <b-dropdown aria-role="list">
                  <button slot="trigger" class="button is-primary is-small">
                    <span>Actions</span>
                    <b-icon icon="menu-down"></b-icon>
                  </button>

                  <b-dropdown-item
                    aria-role="listitem"
                    @click="testCredentialConnection(props.row)"
                  >
                    <b-icon icon="test-tube"></b-icon>
                    Test Connection
                  </b-dropdown-item>

                  <b-dropdown-item
                    aria-role="listitem"
                    @click="openEditCredentialModal(props.row)"
                  >
                    <b-icon icon="pencil"></b-icon>
                    Edit
                  </b-dropdown-item>

                  <b-dropdown-item
                    aria-role="listitem"
                    @click="toggleCredentialActive(props.row)"
                  >
                    <b-icon :icon="props.row.is_active ? 'pause-circle' : 'play-circle'"></b-icon>
                    {{ props.row.is_active ? 'Deactivate' : 'Activate' }}
                  </b-dropdown-item>

                  <b-dropdown-item
                    aria-role="listitem"
                    @click="confirmDeleteCredential(props.row)"
                  >
                    <b-icon icon="delete"></b-icon>
                    Delete
                  </b-dropdown-item>
                </b-dropdown>
              </b-table-column>
            </template>

            <template slot="empty">
              <section class="section">
                <div class="content has-text-grey has-text-centered">
                  <p>
                    <b-icon icon="cloud-off-outline" size="is-large"></b-icon>
                  </p>
                  <p>No storage credentials yet.</p>
                  <p>Add credentials to enable cloud storage access.</p>
                </div>
              </section>
            </template>
          </b-table>
        </div>
      </CardComponent>

      <!-- Encoding Presets Section -->
      <CardComponent class="has-margin-top-30">
        <div class="section">
          <h1 class="title">Encoding Presets</h1>
          <p class="subtitle">
            Define video and audio encoding settings for processing jobs
          </p>

          <div class="has-text-right">
            <b-button
              type="is-primary"
              icon-left="plus"
              @click="openCreatePresetModal"
            >
              Add Encoding Preset
            </b-button>
          </div>

          <b-table
            :data="encodingPresets"
            :bordered="false"
            :striped="true"
            :narrowed="true"
            :hoverable="true"
            :loading="loadingPresets"
            :focusable="false"
            :mobile-cards="true"
            class="has-margin-top-15"
          >
            <template slot-scope="props">
              <b-table-column field="preset_name" label="Name">
                <span class="has-text-weight-semibold">{{ props.row.preset_name }}</span>
                <b-tag v-if="props.row.is_default" type="is-success" size="is-small" class="ml-2">
                  Default
                </b-tag>
              </b-table-column>

              <b-table-column field="preset_code" label="Code">
                <code>{{ props.row.preset_code }}</code>
              </b-table-column>

              <b-table-column field="video_codec" label="Video">
                {{ getCodecLabel(props.row.video_codec) }} @ {{ formatBitrate(props.row.video_bitrate_kbps) }}
              </b-table-column>

              <b-table-column field="resolution" label="Resolution">
                {{ props.row.width }}x{{ props.row.height }}
                <span class="has-text-grey">{{ props.row.interlaced ? 'i' : 'p' }}</span>
              </b-table-column>

              <b-table-column field="audio_codec" label="Audio">
                {{ props.row.audio_codec.toUpperCase() }} {{ props.row.audio_bitrate_kbps }}kbps
              </b-table-column>

              <b-table-column field="loudness" label="Loudness">
                <span v-if="props.row.apply_loudness_normalization">
                  {{ props.row.loudness_target_lufs }} LUFS
                </span>
                <span v-else class="has-text-grey">Off</span>
              </b-table-column>

              <b-table-column class="has-text-centered" label="Actions">
                <b-dropdown aria-role="list">
                  <button slot="trigger" class="button is-primary is-small">
                    <span>Actions</span>
                    <b-icon icon="menu-down"></b-icon>
                  </button>

                  <b-dropdown-item
                    v-if="!props.row.is_default"
                    aria-role="listitem"
                    @click="setPresetAsDefault(props.row)"
                  >
                    <b-icon icon="star"></b-icon>
                    Set as Default
                  </b-dropdown-item>

                  <b-dropdown-item
                    aria-role="listitem"
                    @click="openEditPresetModal(props.row)"
                  >
                    <b-icon icon="pencil"></b-icon>
                    Edit
                  </b-dropdown-item>

                  <b-dropdown-item
                    aria-role="listitem"
                    @click="duplicatePreset(props.row)"
                  >
                    <b-icon icon="content-copy"></b-icon>
                    Duplicate
                  </b-dropdown-item>

                  <b-dropdown-item
                    aria-role="listitem"
                    @click="confirmDeletePreset(props.row)"
                  >
                    <b-icon icon="delete"></b-icon>
                    Delete
                  </b-dropdown-item>
                </b-dropdown>
              </b-table-column>
            </template>

            <template slot="empty">
              <section class="section">
                <div class="content has-text-grey has-text-centered">
                  <p>
                    <b-icon icon="cog-outline" size="is-large"></b-icon>
                  </p>
                  <p>No encoding presets yet.</p>
                  <p>Add presets to define encoding settings for processing jobs.</p>
                </div>
              </section>
            </template>
          </b-table>
        </div>
      </CardComponent>

      <!-- Processing Workflows Section -->
      <CardComponent class="has-margin-top-30">
        <div class="section">
          <h1 class="title">Processing Workflows</h1>
          <p class="subtitle">
            Create and manage named workflows for processing jobs. Each workflow defines input/output storage and encoding settings.
          </p>

          <div class="has-text-right">
            <b-button
              type="is-primary"
              icon-left="plus"
              @click="openCreateWorkflowModal"
            >
              Add Workflow
            </b-button>
          </div>

          <b-table
            :data="processingWorkflows"
            :bordered="false"
            :striped="true"
            :narrowed="true"
            :hoverable="true"
            :loading="loadingProcessingWorkflows"
            :focusable="false"
            :mobile-cards="true"
            class="has-margin-top-15"
          >
            <template slot-scope="props">
              <b-table-column field="workflow_name" label="Name">
                <span class="has-text-weight-semibold">{{ props.row.workflow_name }}</span>
                <b-tag v-if="props.row.is_default" type="is-success" size="is-small" class="ml-2">
                  Default
                </b-tag>
              </b-table-column>

              <b-table-column field="platform_template_name" label="Platform Template">
                <template v-if="props.row.platform_template_name">
                  <b-tag type="is-link" size="is-small">
                    {{ props.row.platform_template_name }}
                  </b-tag>
                </template>
                <span v-else class="has-text-grey">-</span>
              </b-table-column>

              <b-table-column field="workflow_type" label="Type" width="100">
                <b-tag :type="getWorkflowTypeTagType(props.row.workflow_type)" size="is-small">
                  {{ getWorkflowTypeLabel(props.row.workflow_type) }}
                </b-tag>
              </b-table-column>

              <b-table-column field="input_storage" label="Input">
                {{ props.row.input_config_name || '-' }}
              </b-table-column>

              <b-table-column field="output_storage" label="Output">
                {{ props.row.output_config_name || '-' }}
              </b-table-column>

              <b-table-column field="encoding_preset" label="Encoding">
                {{ props.row.encoding_preset_name || '-' }}
              </b-table-column>

              <b-table-column field="priority" label="Priority">
                <b-tag :type="getPriorityTagType(props.row.priority)" size="is-small">
                  {{ props.row.priority }}
                </b-tag>
              </b-table-column>

              <b-table-column class="has-text-centered" label="Actions">
                <b-dropdown aria-role="list">
                  <button slot="trigger" class="button is-primary is-small">
                    <span>Actions</span>
                    <b-icon icon="menu-down"></b-icon>
                  </button>

                  <b-dropdown-item
                    v-if="!props.row.is_default"
                    aria-role="listitem"
                    @click="setWorkflowAsDefault(props.row)"
                  >
                    <b-icon icon="star"></b-icon>
                    Set as Default
                  </b-dropdown-item>

                  <b-dropdown-item
                    aria-role="listitem"
                    @click="openEditWorkflowModal(props.row)"
                  >
                    <b-icon icon="pencil"></b-icon>
                    Edit
                  </b-dropdown-item>

                  <b-dropdown-item
                    aria-role="listitem"
                    @click="confirmDeleteWorkflow(props.row)"
                  >
                    <b-icon icon="delete"></b-icon>
                    Delete
                  </b-dropdown-item>
                </b-dropdown>
              </b-table-column>
            </template>

            <template slot="empty">
              <section class="section">
                <div class="content has-text-grey has-text-centered">
                  <p>
                    <b-icon icon="transit-connection-variant" size="is-large"></b-icon>
                  </p>
                  <p>No workflows yet.</p>
                  <p>Create a workflow to define processing pipelines.</p>
                </div>
              </section>
            </template>
          </b-table>
        </div>
      </CardComponent>

      <!-- Available Platform Workflows Section (Read-Only) -->
      <CardComponent class="has-margin-top-30">
        <div class="section">
          <h1 class="title">Available Platform Workflows</h1>
          <p class="subtitle">
            Processing workflows available to your organization. Contact your administrator to enable additional workflows.
          </p>

          <b-table
            :data="platformWorkflows"
            :bordered="false"
            :striped="true"
            :narrowed="true"
            :hoverable="true"
            :loading="loadingPlatformWorkflows"
            :focusable="false"
            :mobile-cards="true"
            class="has-margin-top-15"
          >
            <template slot-scope="props">
              <b-table-column field="template_name" label="Workflow">
                <span class="has-text-weight-semibold">{{ props.row.template_name }}</span>
                <b-tag v-if="props.row.is_beta" type="is-warning" size="is-small" class="ml-2">
                  Beta
                </b-tag>
              </b-table-column>

              <b-table-column field="workflow_category" label="Category" width="100">
                <b-tag :type="getWorkflowCategoryTagType(props.row.workflow_category)" size="is-small">
                  {{ getWorkflowCategoryLabel(props.row.workflow_category) }}
                </b-tag>
              </b-table-column>

              <b-table-column field="description" label="Description">
                <span class="is-size-7">{{ props.row.description }}</span>
              </b-table-column>

              <b-table-column field="supported_input_formats" label="Input Formats">
                <span class="is-size-7">{{ formatInputFormats(props.row.supported_input_formats) }}</span>
              </b-table-column>

              <b-table-column field="supported_output_formats" label="Output Formats">
                <span class="is-size-7">{{ formatOutputFormats(props.row.supported_output_formats) }}</span>
              </b-table-column>

              <b-table-column field="includes_qc" label="QC" width="60">
                <b-icon
                  :icon="props.row.includes_qc ? 'check-circle' : 'close-circle'"
                  :type="props.row.includes_qc ? 'is-success' : 'is-light'"
                  size="is-small"
                ></b-icon>
              </b-table-column>

              <b-table-column field="cost_multiplier" label="Cost" width="70">
                <b-tag
                  :type="props.row.cost_multiplier <= 0.5 ? 'is-success' : props.row.cost_multiplier >= 1.5 ? 'is-warning' : 'is-light'"
                  size="is-small"
                >
                  {{ props.row.cost_multiplier }}x
                </b-tag>
              </b-table-column>
            </template>

            <template slot="empty">
              <section class="section">
                <div class="content has-text-grey has-text-centered">
                  <p>
                    <b-icon icon="lock" size="is-large"></b-icon>
                  </p>
                  <p>No workflows available yet.</p>
                  <p>Contact your administrator to enable platform workflows for your organization.</p>
                </div>
              </section>
            </template>
          </b-table>

          <div v-if="platformWorkflows.length > 0" class="has-text-grey is-size-7 has-margin-top-15">
            <b-icon icon="information" size="is-small"></b-icon>
            These workflows are managed at the platform level. To request access to additional workflows or modify limits, contact your system administrator.
          </div>
        </div>
      </CardComponent>

      <!-- Storage Path Configurations Section -->
      <CardComponent class="has-margin-top-30">
        <div class="section">
          <h1 class="title">Storage Path Configurations</h1>
          <p class="subtitle">
            Configure folder paths for archive, library, TX mezzanine, and VOD delivery
          </p>

          <div class="has-text-right">
            <b-button
              type="is-primary"
              icon-left="plus"
              @click="openCreateConfigModal"
            >
              Add Path Configuration
            </b-button>
          </div>

          <b-table
            :data="storageConfigs"
            :bordered="false"
            :striped="true"
            :narrowed="true"
            :hoverable="true"
            :loading="loadingConfigs"
            :focusable="false"
            :mobile-cards="true"
            class="has-margin-top-15"
          >
            <template slot-scope="props">
              <b-table-column field="config_name" label="Purpose">
                <b-tag :type="getConfigTagType(props.row.config_name)">
                  {{ formatConfigName(props.row.config_name) }}
                </b-tag>
              </b-table-column>

              <b-table-column field="bucket_name" label="Bucket">
                <code>{{ props.row.bucket_name || '-' }}</code>
              </b-table-column>

              <b-table-column field="storage_credential" label="Credential">
                {{ getCredentialName(props.row.storage_credential_id) }}
              </b-table-column>

              <b-table-column field="base_folder" label="Base Folder">
                <code>{{ props.row.base_folder || '/' }}</code>
              </b-table-column>

              <b-table-column field="folder_template" label="Folder Template">
                <code v-if="props.row.folder_template">{{ props.row.folder_template }}</code>
                <span v-else class="has-text-grey">-</span>
              </b-table-column>

              <b-table-column field="default_codec" label="Default Codec">
                {{ props.row.default_codec || '-' }}
              </b-table-column>

              <b-table-column field="processing_location" label="Processing">
                <b-tag v-if="props.row.processing_location" :type="getProcessingTagType(props.row.processing_location)">
                  {{ getProcessingLabel(props.row.processing_location) }}
                </b-tag>
                <span v-else class="has-text-grey">-</span>
              </b-table-column>

              <b-table-column field="is_active" label="Status">
                <b-tag :type="props.row.is_active ? 'is-success' : 'is-danger'">
                  {{ props.row.is_active ? 'Active' : 'Inactive' }}
                </b-tag>
              </b-table-column>

              <b-table-column class="has-text-centered" label="Actions">
                <b-dropdown aria-role="list">
                  <button slot="trigger" class="button is-primary is-small">
                    <span>Actions</span>
                    <b-icon icon="menu-down"></b-icon>
                  </button>

                  <b-dropdown-item
                    aria-role="listitem"
                    @click="testConfigConnection(props.row)"
                  >
                    <b-icon icon="test-tube"></b-icon>
                    Test Path
                  </b-dropdown-item>

                  <b-dropdown-item
                    aria-role="listitem"
                    @click="openEditConfigModal(props.row)"
                  >
                    <b-icon icon="pencil"></b-icon>
                    Edit
                  </b-dropdown-item>

                  <b-dropdown-item
                    aria-role="listitem"
                    @click="confirmDeleteConfig(props.row)"
                  >
                    <b-icon icon="delete"></b-icon>
                    Delete
                  </b-dropdown-item>
                </b-dropdown>
              </b-table-column>
            </template>

            <template slot="empty">
              <section class="section">
                <div class="content has-text-grey has-text-centered">
                  <p>
                    <b-icon icon="folder-outline" size="is-large"></b-icon>
                  </p>
                  <p>No path configurations yet.</p>
                  <p>Add configurations to define storage paths for different purposes.</p>
                </div>
              </section>
            </template>
          </b-table>
        </div>
      </CardComponent>

      <!-- S3 Upload Watchers Section -->
      <CardComponent class="has-margin-top-30">
        <div class="section">
          <h1 class="title">
            <b-icon icon="eye" class="mr-2"></b-icon>
            S3 Folder Watchers
          </h1>
          <p class="subtitle">
            Configure folders to watch for new uploads. Email notifications will be sent when new files are detected.
          </p>

          <div class="level mb-4">
            <div class="level-left">
              <div class="level-item">
                <span class="tag is-info is-medium">{{ s3Watchers.length }} Watcher(s)</span>
              </div>
            </div>
            <div class="level-right">
              <div class="level-item">
                <b-button
                  type="is-primary"
                  icon-left="plus"
                  @click="openCreateWatcherModal"
                  :disabled="storageConfigs.length === 0"
                >
                  Add Watcher
                </b-button>
              </div>
            </div>
          </div>

          <b-table
            :data="s3Watchers"
            :bordered="false"
            :striped="true"
            :narrowed="true"
            :hoverable="true"
            :loading="loadingS3Watchers"
            :focusable="false"
            :mobile-cards="true"
          >
            <template slot-scope="props">
              <b-table-column field="storage_config_id" label="Storage">
                <span class="has-text-weight-semibold">{{ getStorageConfigName(props.row.storage_config_id) }}</span>
              </b-table-column>

              <b-table-column field="watch_folder_path" label="Watch Folder">
                <code class="is-size-7">{{ props.row.watch_folder_path || '/' }}</code>
              </b-table-column>

              <b-table-column field="file_extensions" label="File Types">
                <div class="tags">
                  <span
                    v-for="ext in (props.row.file_extensions || 'mp4,mxf,mov').split(',')"
                    :key="ext"
                    class="tag is-small"
                  >
                    .{{ ext.trim() }}
                  </span>
                </div>
              </b-table-column>

              <b-table-column field="notification_emails" label="Notify">
                <span class="is-size-7">{{ props.row.notification_emails || '-' }}</span>
              </b-table-column>

              <b-table-column field="is_active" label="Status" centered width="80">
                <b-tag :type="props.row.is_active ? 'is-success' : 'is-warning'">
                  {{ props.row.is_active ? 'Active' : 'Paused' }}
                </b-tag>
              </b-table-column>

              <b-table-column label="Actions" width="150" centered>
                <b-button
                  type="is-info"
                  size="is-small"
                  icon-left="pencil"
                  @click="openEditWatcherModal(props.row)"
                  title="Edit"
                ></b-button>
                <b-button
                  type="is-warning"
                  size="is-small"
                  icon-left="database-sync"
                  @click="preSeedWatcher(props.row.id)"
                  title="Pre-seed existing files"
                  class="ml-1"
                ></b-button>
                <b-button
                  type="is-danger"
                  size="is-small"
                  icon-left="delete"
                  @click="confirmDeleteWatcher(props.row)"
                  title="Delete"
                  class="ml-1"
                ></b-button>
              </b-table-column>
            </template>

            <template slot="empty">
              <section class="section">
                <div class="content has-text-grey has-text-centered">
                  <p>
                    <b-icon icon="eye-off" size="is-large"></b-icon>
                  </p>
                  <p>No S3 folder watchers configured.</p>
                  <p v-if="storageConfigs.length === 0" class="has-text-warning">
                    You need to create a storage configuration first.
                  </p>
                  <p v-else>
                    Click "Add Watcher" to start monitoring a folder for uploads.
                  </p>
                </div>
              </section>
            </template>
          </b-table>
        </div>
      </CardComponent>

      <!-- S3 Upload Detections Section -->
      <CardComponent class="has-margin-top-30">
        <div class="section">
          <h1 class="title">
            <b-icon icon="cloud-upload-outline" class="mr-2"></b-icon>
            S3 Upload Detections
          </h1>
          <p class="subtitle">
            Files detected in watched S3 folders. These files can be linked to episodes.
          </p>

          <div class="level">
            <div class="level-left">
              <div class="level-item">
                <b-field label="Show Last">
                  <b-select v-model="s3DetectionsDays" @input="refreshS3Detections">
                    <option :value="1">24 Hours</option>
                    <option :value="7">7 Days</option>
                    <option :value="14">14 Days</option>
                    <option :value="30">30 Days</option>
                  </b-select>
                </b-field>
              </div>
            </div>
            <div class="level-right">
              <div class="level-item">
                <b-button
                  type="is-light"
                  icon-left="refresh"
                  :loading="loadingS3Detections"
                  @click="refreshS3Detections"
                >
                  Refresh
                </b-button>
              </div>
            </div>
          </div>

          <b-table
            :data="s3Detections"
            :bordered="false"
            :striped="true"
            :narrowed="true"
            :hoverable="true"
            :loading="loadingS3Detections"
            :focusable="false"
            :mobile-cards="true"
            :paginated="s3Detections.length > 25"
            :per-page="25"
            default-sort="detected_at"
            default-sort-direction="desc"
          >
            <template slot-scope="props">
              <b-table-column field="filename" label="Filename" sortable>
                <span class="has-text-weight-semibold">{{ props.row.filename }}</span>
              </b-table-column>

              <b-table-column field="file_size_bytes" label="Size" sortable width="100">
                {{ formatFileSize(props.row.file_size_bytes) }}
              </b-table-column>

              <b-table-column field="bucket_name" label="Bucket">
                <code class="is-size-7">{{ props.row.bucket_name }}</code>
              </b-table-column>

              <b-table-column field="file_key" label="Path">
                <span class="is-size-7 has-text-grey">
                  {{ props.row.file_key.split('/').slice(0, -1).join('/') || '/' }}
                </span>
              </b-table-column>

              <b-table-column field="detected_at" label="Detected" sortable width="160">
                {{ formatDetectionDate(props.row.detected_at) }}
              </b-table-column>

              <b-table-column field="notification_sent" label="Notified" centered width="80">
                <b-icon
                  :icon="props.row.notification_sent ? 'check-circle' : 'clock-outline'"
                  :type="props.row.notification_sent ? 'is-success' : 'is-warning'"
                  size="is-small"
                ></b-icon>
              </b-table-column>
            </template>

            <template slot="empty">
              <section class="section">
                <div class="content has-text-grey has-text-centered">
                  <p>
                    <b-icon icon="cloud-off-outline" size="is-large"></b-icon>
                  </p>
                  <p>No file detections in the selected time period.</p>
                  <p v-if="s3Watchers.length === 0" class="has-text-warning">
                    No S3 watchers are configured for your organization.
                  </p>
                </div>
              </section>
            </template>
          </b-table>

          <div v-if="s3Watchers.length === 0" class="notification is-warning is-light mt-4">
            <b-icon icon="alert" size="is-small"></b-icon>
            No S3 folder watchers are configured. Use the "S3 Folder Watchers" section above to set up file detection.
          </div>
        </div>
      </CardComponent>
    </section>

    <!-- Create/Edit Watcher Modal -->
    <b-modal
      :active.sync="showWatcherModal"
      trap-focus
      aria-role="dialog"
      aria-modal
      has-modal-card
      @close="closeWatcherModal"
    >
      <div class="modal-card" style="width: 600px">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ isEditingWatcher ? 'Edit S3 Watcher' : 'Add S3 Watcher' }}
          </p>
        </header>
        <section class="modal-card-body">
          <b-field label="Storage Configuration *">
            <b-select v-model="watcherForm.storage_config_id" placeholder="Select a storage configuration" expanded required>
              <option v-for="config in storageConfigs" :key="config.id" :value="config.id">
                {{ formatConfigName(config.config_name) }} ({{ config.bucket_name }})
              </option>
            </b-select>
          </b-field>

          <b-field label="Watch Folder Path" message="Leave empty to watch the entire bucket. Use 'uploads/' to watch a specific folder.">
            <b-input v-model="watcherForm.watch_folder_path" placeholder="uploads/" icon="folder"></b-input>
          </b-field>

          <b-field label="File Extensions" message="Comma-separated list of file extensions to monitor (e.g., mp4,mxf,mov)">
            <b-input v-model="watcherForm.file_extensions" placeholder="mp4,mxf,mov,ts,m2ts" icon="file-video"></b-input>
          </b-field>

          <b-field label="Notification Email(s)" message="Comma-separated list of email addresses to notify when new files are detected">
            <b-input v-model="watcherForm.notification_emails" placeholder="user@example.com" icon="email"></b-input>
          </b-field>

          <b-field>
            <b-switch v-model="watcherForm.is_active" type="is-success">
              {{ watcherForm.is_active ? 'Watcher is Active' : 'Watcher is Paused' }}
            </b-switch>
          </b-field>
        </section>
        <footer class="modal-card-foot">
          <b-button label="Cancel" @click="closeWatcherModal"></b-button>
          <b-button
            :label="isEditingWatcher ? 'Save Changes' : 'Create Watcher'"
            type="is-primary"
            :loading="savingWatcher"
            @click="saveWatcher"
            :disabled="!watcherForm.storage_config_id"
          ></b-button>
        </footer>
      </div>
    </b-modal>

    <!-- Create/Edit Credential Modal -->
    <b-modal
      :active.sync="showCredentialModal"
      trap-focus
      aria-role="dialog"
      aria-modal
      has-modal-card
      @close="closeCredentialModal"
    >
      <div class="modal-card" style="width: 600px">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ isEditingCredential ? 'Edit Storage Credential' : 'Add Storage Credential' }}
          </p>
        </header>
        <section class="modal-card-body">
          <b-field label="Storage Name *">
            <b-input
              v-model="credentialForm.storage_name"
              placeholder="e.g., Master Storage, Playout Storage"
              required
            />
          </b-field>

          <b-field label="Provider *">
            <b-select
              v-model="credentialForm.storage_provider"
              placeholder="Select storage provider"
              expanded
              required
              @input="onProviderChange"
            >
              <optgroup label="Cloud Storage">
                <option value="s3">Amazon S3</option>
                <option value="s3_compatible">S3 Compatible (MinIO, Backblaze, etc.)</option>
                <option value="huawei_obs">Huawei OBS</option>
                <option value="wasabi">Wasabi</option>
                <option value="google">Google Cloud Storage</option>
                <option value="azure">Azure Blob Storage</option>
              </optgroup>
              <optgroup label="Network Storage">
                <option value="smb">SMB/CIFS (Windows Share)</option>
                <option value="nfs">NFS (Network File System)</option>
                <option value="sftp">SFTP/FTP</option>
              </optgroup>
              <optgroup label="Local Storage">
                <option value="local">Local Filesystem</option>
                <option value="nas">NAS (Mounted Path)</option>
              </optgroup>
            </b-select>
          </b-field>

          <!-- S3/Wasabi/Google Cloud Fields -->
          <div v-if="['s3', 's3_compatible', 'huawei_obs', 'wasabi', 'google', 'azure'].includes(credentialForm.storage_provider)">
            <b-field label="Bucket Name *">
              <b-input
                v-model="credentialForm.bucket_name"
                placeholder="my-bucket-name"
                required
              />
            </b-field>

            <b-field label="Region">
              <b-select v-model="credentialForm.region" placeholder="Select region" expanded @input="onRegionChange">
                <!-- AWS S3 Regions -->
                <optgroup v-if="credentialForm.storage_provider === 's3'" label="AWS Regions">
                  <option value="us-east-1">US East (N. Virginia)</option>
                  <option value="us-east-2">US East (Ohio)</option>
                  <option value="us-west-1">US West (N. California)</option>
                  <option value="us-west-2">US West (Oregon)</option>
                  <option value="ca-central-1">Canada (Central)</option>
                  <option value="eu-west-1">EU (Ireland)</option>
                  <option value="eu-west-2">EU (London)</option>
                  <option value="eu-west-3">EU (Paris)</option>
                  <option value="eu-central-1">EU (Frankfurt)</option>
                  <option value="eu-north-1">EU (Stockholm)</option>
                  <option value="eu-south-1">EU (Milan)</option>
                  <option value="af-south-1">Africa (Cape Town)</option>
                  <option value="ap-east-1">Asia Pacific (Hong Kong)</option>
                  <option value="ap-south-1">Asia Pacific (Mumbai)</option>
                  <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                  <option value="ap-southeast-2">Asia Pacific (Sydney)</option>
                  <option value="ap-northeast-1">Asia Pacific (Tokyo)</option>
                  <option value="ap-northeast-2">Asia Pacific (Seoul)</option>
                  <option value="ap-northeast-3">Asia Pacific (Osaka)</option>
                  <option value="sa-east-1">South America (São Paulo)</option>
                  <option value="me-south-1">Middle East (Bahrain)</option>
                </optgroup>
                <!-- Wasabi Regions -->
                <optgroup v-if="credentialForm.storage_provider === 'wasabi'" label="Wasabi Regions">
                  <option value="us-east-1">US East 1 (N. Virginia)</option>
                  <option value="us-east-2">US East 2 (N. Virginia)</option>
                  <option value="us-west-1">US West 1 (Oregon)</option>
                  <option value="us-central-1">US Central 1 (Texas)</option>
                  <option value="ca-central-1">Canada (Toronto)</option>
                  <option value="eu-central-1">EU Central 1 (Amsterdam)</option>
                  <option value="eu-central-2">EU Central 2 (Frankfurt)</option>
                  <option value="eu-west-1">EU West 1 (London)</option>
                  <option value="eu-west-2">EU West 2 (Paris)</option>
                  <option value="ap-northeast-1">AP Northeast 1 (Tokyo)</option>
                  <option value="ap-northeast-2">AP Northeast 2 (Osaka)</option>
                  <option value="ap-southeast-1">AP Southeast 1 (Singapore)</option>
                  <option value="ap-southeast-2">AP Southeast 2 (Sydney)</option>
                </optgroup>
                <!-- Huawei OBS Regions -->
                <optgroup v-if="credentialForm.storage_provider === 'huawei_obs'" label="Huawei OBS Regions">
                  <option value="af-south-1">Africa (Johannesburg)</option>
                  <option value="cn-north-1">China North (Beijing)</option>
                  <option value="cn-north-4">China North (Beijing 4)</option>
                  <option value="cn-east-2">China East (Shanghai)</option>
                  <option value="cn-east-3">China East (Shanghai 3)</option>
                  <option value="cn-south-1">China South (Guangzhou)</option>
                  <option value="ap-southeast-1">Asia Pacific (Hong Kong)</option>
                  <option value="ap-southeast-2">Asia Pacific (Bangkok)</option>
                  <option value="ap-southeast-3">Asia Pacific (Singapore)</option>
                  <option value="la-south-2">Latin America (Santiago)</option>
                  <option value="sa-brazil-1">South America (Sao Paulo)</option>
                  <option value="na-mexico-1">North America (Mexico City)</option>
                  <option value="la-north-2">Latin America (Mexico City 2)</option>
                  <option value="eu-west-0">Europe (Paris)</option>
                  <option value="eu-west-101">Europe (Dublin)</option>
                </optgroup>
                <!-- Google Cloud Regions -->
                <optgroup v-if="credentialForm.storage_provider === 'google'" label="Google Cloud Regions">
                  <option value="us-central1">US Central</option>
                  <option value="us-east1">US East</option>
                  <option value="us-west1">US West</option>
                  <option value="europe-west1">Europe West</option>
                  <option value="asia-east1">Asia East</option>
                </optgroup>
                <!-- S3-Compatible - use AWS regions as reference -->
                <optgroup v-if="credentialForm.storage_provider === 's3_compatible'" label="Common Regions">
                  <option value="us-east-1">US East 1</option>
                  <option value="us-west-1">US West 1</option>
                  <option value="eu-west-1">EU West 1</option>
                  <option value="eu-central-1">EU Central 1</option>
                  <option value="ap-southeast-1">AP Southeast 1</option>
                  <option value="custom">Custom (specify in endpoint)</option>
                </optgroup>
              </b-select>
              <p v-if="credentialForm.storage_provider === 'wasabi' || credentialForm.storage_provider === 'huawei_obs'" class="help">
                Endpoint URL will auto-update based on selected region
              </p>
            </b-field>

            <b-field>
              <b-checkbox v-model="credentialForm.is_public">
                Public Bucket (no credentials needed)
              </b-checkbox>
            </b-field>

            <div v-if="!credentialForm.is_public">
              <b-field :label="credentialForm.storage_provider === 'google' ? 'Client Email *' : 'Access Key ID *'">
                <b-input
                  v-model="credentialForm.access_key_id"
                  :placeholder="credentialForm.storage_provider === 'google' ? 'service-account@project.iam.gserviceaccount.com' : 'AKIAIOSFODNN7EXAMPLE'"
                  type="password"
                  password-reveal
                  required
                />
              </b-field>

              <b-field :label="credentialForm.storage_provider === 'google' ? 'Private Key *' : 'Secret Access Key *'">
                <b-input
                  v-model="credentialForm.secret_access_key"
                  :placeholder="credentialForm.storage_provider === 'google' ? '-----BEGIN PRIVATE KEY-----...' : 'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY'"
                  type="password"
                  password-reveal
                  required
                />
              </b-field>
            </div>

            <b-field v-if="credentialForm.storage_provider === 'wasabi'" label="Endpoint URL (Auto-populated)">
              <b-input
                v-model="credentialForm.endpoint_url"
                placeholder="https://s3.us-east-1.wasabisys.com"
                :disabled="true"
              />
              <p class="help">
                <b-icon icon="check-circle" size="is-small" type="is-success" />
                Auto-populated based on selected region. Change region to update endpoint.
              </p>
            </b-field>

            <b-field v-if="credentialForm.storage_provider === 's3_compatible'" label="Endpoint URL *">
              <b-input
                v-model="credentialForm.endpoint_url"
                placeholder="https://s3.example.com or https://nyc3.digitaloceanspaces.com"
                required
              />
              <p class="help">Required: MinIO, DigitalOcean Spaces, Backblaze B2, Cloudflare R2, etc.</p>
            </b-field>

            <b-field v-if="credentialForm.storage_provider === 'huawei_obs'" label="Endpoint URL (Auto-populated)">
              <b-input
                v-model="credentialForm.endpoint_url"
                placeholder="https://obs.af-south-1.myhuaweicloud.com"
                :disabled="true"
              />
              <p class="help">
                <b-icon icon="check-circle" size="is-small" type="is-success" />
                Auto-populated based on selected region. Format: obs.{region}.myhuaweicloud.com
              </p>
            </b-field>

            <b-field v-if="credentialForm.storage_provider === 's3'" label="Custom Endpoint (Optional)">
              <b-input
                v-model="credentialForm.endpoint_url"
                placeholder="https://nyc3.digitaloceanspaces.com"
              />
              <p class="help">Only needed for non-AWS S3-compatible services</p>
            </b-field>
          </div>

          <!-- Local/NAS Fields -->
          <div
            v-if="
              credentialForm.storage_provider === 'local' ||
                credentialForm.storage_provider === 'nas'
            "
          >
            <b-field label="Base Path *">
              <b-input
                v-model="credentialForm.base_path"
                placeholder="/mnt/nas/media"
                required
              />
              <p class="help">Absolute path to storage root directory</p>
            </b-field>
          </div>

          <!-- SMB/CIFS Fields -->
          <div v-if="credentialForm.storage_provider === 'smb'">
            <b-field label="Network Path *">
              <b-input
                v-model="credentialForm.network_path"
                placeholder="//server/share or \\\\server\\share"
                required
              />
              <p class="help">UNC path to the SMB share (e.g., //192.168.1.100/media or //fileserver.local/tx-output)</p>
            </b-field>

            <b-field label="Domain (Optional)">
              <b-input
                v-model="credentialForm.smb_domain"
                placeholder="WORKGROUP or MYDOMAIN"
              />
              <p class="help">Windows domain name. Leave empty for workgroup environments.</p>
            </b-field>

            <b-field label="Username *">
              <b-input
                v-model="credentialForm.network_username"
                placeholder="mediauser"
                required
              />
            </b-field>

            <b-field label="Password *">
              <b-input
                v-model="credentialForm.network_password"
                type="password"
                password-reveal
                placeholder="Enter password"
                required
              />
            </b-field>
          </div>

          <!-- NFS Fields -->
          <div v-if="credentialForm.storage_provider === 'nfs'">
            <b-field label="NFS Export Path *">
              <b-input
                v-model="credentialForm.network_path"
                placeholder="server:/export/media"
                required
              />
              <p class="help">NFS server and export path (e.g., 192.168.1.100:/export/media or nfs.local:/vol/tx-output)</p>
            </b-field>

            <b-field label="Mount Options (Optional)">
              <b-input
                v-model="credentialForm.nfs_mount_options"
                placeholder="rw,sync,no_subtree_check"
              />
              <p class="help">Comma-separated NFS mount options. Common: rw, sync, hard, intr</p>
            </b-field>
          </div>

          <!-- SFTP/FTP Fields -->
          <div v-if="credentialForm.storage_provider === 'sftp'">
            <div class="columns">
              <div class="column is-8">
                <b-field label="Host *">
                  <b-input
                    v-model="credentialForm.ftp_host"
                    placeholder="ftp.example.com or 192.168.1.100"
                    required
                  />
                </b-field>
              </div>
              <div class="column is-4">
                <b-field label="Port">
                  <b-input
                    v-model.number="credentialForm.ftp_port"
                    type="number"
                    placeholder="22"
                  />
                  <p class="help">Default: 22 (SFTP) or 21 (FTP)</p>
                </b-field>
              </div>
            </div>

            <b-field>
              <b-checkbox v-model="credentialForm.ftp_use_sftp">
                Use SFTP (SSH File Transfer Protocol) - Recommended
              </b-checkbox>
            </b-field>

            <b-field label="Username *">
              <b-input
                v-model="credentialForm.network_username"
                placeholder="mediauser"
                required
              />
            </b-field>

            <b-field label="Password *">
              <b-input
                v-model="credentialForm.network_password"
                type="password"
                password-reveal
                placeholder="Enter password"
                required
              />
              <p class="help">For SFTP, you can also use SSH key authentication (configure on server)</p>
            </b-field>

            <b-field label="Base Directory">
              <b-input
                v-model="credentialForm.base_path"
                placeholder="/home/mediauser/tx-output"
              />
              <p class="help">Remote directory to use as base path (optional)</p>
            </b-field>
          </div>

          <!-- IAM Permissions Help (S3/Wasabi) -->
          <b-collapse
            v-if="['s3', 's3_compatible', 'huawei_obs', 'wasabi'].includes(credentialForm.storage_provider)"
            class="card has-margin-bottom-15"
            animation="slide"
            :open="showIamHelp"
            @open="showIamHelp = true"
            @close="showIamHelp = false"
          >
            <template #trigger="props">
              <div class="card-header" role="button">
                <p class="card-header-title">
                  <b-icon icon="shield-key-outline" />
                  &nbsp;Minimum Required IAM Permissions
                </p>
                <a class="card-header-icon">
                  <b-icon :icon="props.open ? 'menu-up' : 'menu-down'" />
                </a>
              </div>
            </template>
            <div class="card-content">
              <p class="has-margin-bottom-10">
                <strong>Your IAM user/role needs these permissions on the bucket:</strong>
              </p>
              <pre class="iam-policy-code">{{ iamPolicyJson }}</pre>
              <b-message type="is-info" size="is-small" class="has-margin-top-10">
                <strong>What each permission does:</strong>
                <ul style="margin-top: 5px; margin-left: 15px;">
                  <li><code>s3:GetObject</code> - Read source media files</li>
                  <li><code>s3:PutObject</code> - Write processed output files</li>
                  <li><code>s3:DeleteObject</code> - Clean up temporary files</li>
                  <li><code>s3:ListBucket</code> - List files and segments</li>
                </ul>
              </b-message>
              <b-message type="is-warning" size="is-small" class="has-margin-top-10">
                <strong>Important:</strong> Do NOT use <code>s3:*</code> (full access).
                We only need the minimum permissions listed above for security best practices.
              </b-message>
              <b-button
                type="is-primary"
                size="is-small"
                icon-left="content-copy"
                class="has-margin-top-10"
                @click="copyIamPolicy"
              >
                Copy Policy to Clipboard
              </b-button>
            </div>
          </b-collapse>

          <!-- Local Filesystem Setup Guide -->
          <b-collapse
            v-if="['local', 'nas'].includes(credentialForm.storage_provider)"
            class="card has-margin-bottom-15"
            animation="slide"
            :open="showLocalHelp"
            @open="showLocalHelp = true"
            @close="showLocalHelp = false"
          >
            <template #trigger="props">
              <div class="card-header" role="button">
                <p class="card-header-title">
                  <b-icon icon="folder-cog-outline" />
                  &nbsp;Local/NAS Storage Setup Guide
                </p>
                <a class="card-header-icon">
                  <b-icon :icon="props.open ? 'menu-up' : 'menu-down'" />
                </a>
              </div>
            </template>
            <div class="card-content">
              <p class="has-margin-bottom-10">
                <strong>Requirements for Local Filesystem Storage:</strong>
              </p>
              <pre class="setup-guide-code"># Ensure the backend service has read/write access to the path
# If using Docker, mount the volume in docker-compose.yml:

services:
  backend:
    volumes:
      - /path/on/host:/mnt/media:rw

# Set correct permissions:
sudo chown -R www-data:www-data /path/on/host
sudo chmod -R 755 /path/on/host</pre>
              <b-message type="is-info" size="is-small" class="has-margin-top-10">
                <strong>For NAS/Mounted Storage:</strong>
                <ul style="margin-top: 5px; margin-left: 15px;">
                  <li>Ensure the NAS is mounted at boot time (add to <code>/etc/fstab</code>)</li>
                  <li>Use consistent mount points across all application servers</li>
                  <li>Test write permissions before configuring</li>
                </ul>
              </b-message>
              <b-message type="is-warning" size="is-small" class="has-margin-top-10">
                <strong>Cinegy Air / Playout Integration:</strong>
                For playout systems watching a folder, ensure the path is accessible from both:
                <ul style="margin-top: 5px; margin-left: 15px;">
                  <li>This BMS server (for writing TX files)</li>
                  <li>The Cinegy Air playout server (for reading/playing)</li>
                </ul>
              </b-message>
            </div>
          </b-collapse>

          <!-- SMB/CIFS Setup Guide -->
          <b-collapse
            v-if="credentialForm.storage_provider === 'smb'"
            class="card has-margin-bottom-15"
            animation="slide"
            :open="showSmbHelp"
            @open="showSmbHelp = true"
            @close="showSmbHelp = false"
          >
            <template #trigger="props">
              <div class="card-header" role="button">
                <p class="card-header-title">
                  <b-icon icon="microsoft-windows" />
                  &nbsp;SMB/CIFS (Windows Share) Setup Guide
                </p>
                <a class="card-header-icon">
                  <b-icon :icon="props.open ? 'menu-up' : 'menu-down'" />
                </a>
              </div>
            </template>
            <div class="card-content">
              <p class="has-margin-bottom-10">
                <strong>Windows Server Share Configuration:</strong>
              </p>
              <pre class="setup-guide-code"># On Windows Server, create a dedicated share:
# 1. Right-click folder → Properties → Sharing → Advanced Sharing
# 2. Enable "Share this folder"
# 3. Set Share name (e.g., "tx-output")

# Share Permissions (minimum required):
#   - User/Group: Your media user account
#   - Permissions: Change (Read + Write)

# NTFS Permissions on the folder:
#   - User/Group: Your media user account
#   - Permissions: Modify (Read, Write, Delete)</pre>
              <b-message type="is-info" size="is-small" class="has-margin-top-10">
                <strong>Connection Details:</strong>
                <ul style="margin-top: 5px; margin-left: 15px;">
                  <li><strong>Network Path:</strong> <code>//server/share</code> or <code>\\server\share</code></li>
                  <li><strong>Domain:</strong> Required for domain-joined servers (e.g., CONTOSO)</li>
                  <li><strong>Username:</strong> Domain user as <code>DOMAIN\username</code> or just <code>username</code></li>
                </ul>
              </b-message>
              <b-message type="is-warning" size="is-small" class="has-margin-top-10">
                <strong>Firewall Requirements:</strong>
                <ul style="margin-top: 5px; margin-left: 15px;">
                  <li>TCP Port 445 (SMB direct) must be open</li>
                  <li>TCP Port 139 (NetBIOS) for legacy systems</li>
                  <li>Test connectivity: <code>smbclient -L //server -U username</code></li>
                </ul>
              </b-message>
            </div>
          </b-collapse>

          <!-- NFS Setup Guide -->
          <b-collapse
            v-if="credentialForm.storage_provider === 'nfs'"
            class="card has-margin-bottom-15"
            animation="slide"
            :open="showNfsHelp"
            @open="showNfsHelp = true"
            @close="showNfsHelp = false"
          >
            <template #trigger="props">
              <div class="card-header" role="button">
                <p class="card-header-title">
                  <b-icon icon="server-network" />
                  &nbsp;NFS (Network File System) Setup Guide
                </p>
                <a class="card-header-icon">
                  <b-icon :icon="props.open ? 'menu-up' : 'menu-down'" />
                </a>
              </div>
            </template>
            <div class="card-content">
              <p class="has-margin-bottom-10">
                <strong>NFS Server Configuration (Linux):</strong>
              </p>
              <pre class="setup-guide-code"># On NFS Server - Install NFS:
sudo apt install nfs-kernel-server  # Debian/Ubuntu
sudo yum install nfs-utils          # RHEL/CentOS

# Create export directory:
sudo mkdir -p /export/media
sudo chown nobody:nogroup /export/media
sudo chmod 755 /export/media

# Configure exports (/etc/exports):
/export/media  192.168.1.0/24(rw,sync,no_subtree_check,no_root_squash)

# Apply changes:
sudo exportfs -ra
sudo systemctl restart nfs-kernel-server</pre>
              <b-message type="is-info" size="is-small" class="has-margin-top-10">
                <strong>NFS Export Options Explained:</strong>
                <ul style="margin-top: 5px; margin-left: 15px;">
                  <li><code>rw</code> - Read/write access (required for TX delivery)</li>
                  <li><code>sync</code> - Write data before confirming (safer)</li>
                  <li><code>no_subtree_check</code> - Improves reliability</li>
                  <li><code>no_root_squash</code> - Allow root access (use carefully)</li>
                </ul>
              </b-message>
              <b-message type="is-warning" size="is-small" class="has-margin-top-10">
                <strong>Firewall Requirements:</strong>
                <ul style="margin-top: 5px; margin-left: 15px;">
                  <li>TCP/UDP Port 2049 (NFS)</li>
                  <li>TCP/UDP Port 111 (rpcbind/portmapper)</li>
                  <li>Test: <code>showmount -e server_ip</code></li>
                </ul>
              </b-message>
            </div>
          </b-collapse>

          <!-- SFTP Setup Guide -->
          <b-collapse
            v-if="credentialForm.storage_provider === 'sftp'"
            class="card has-margin-bottom-15"
            animation="slide"
            :open="showSftpHelp"
            @open="showSftpHelp = true"
            @close="showSftpHelp = false"
          >
            <template #trigger="props">
              <div class="card-header" role="button">
                <p class="card-header-title">
                  <b-icon icon="ssh" />
                  &nbsp;SFTP/FTP Setup Guide
                </p>
                <a class="card-header-icon">
                  <b-icon :icon="props.open ? 'menu-up' : 'menu-down'" />
                </a>
              </div>
            </template>
            <div class="card-content">
              <p class="has-margin-bottom-10">
                <strong>SFTP Server Configuration (Recommended):</strong>
              </p>
              <pre class="setup-guide-code"># SFTP uses SSH - typically already installed
# Create dedicated user for media transfers:

sudo useradd -m -s /bin/bash mediauser
sudo passwd mediauser

# Create directory for TX files:
sudo mkdir -p /home/mediauser/tx-output
sudo chown mediauser:mediauser /home/mediauser/tx-output

# Optional: Restrict to SFTP only (/etc/ssh/sshd_config):
Match User mediauser
    ChrootDirectory /home/mediauser
    ForceCommand internal-sftp
    AllowTcpForwarding no
    X11Forwarding no</pre>
              <b-message type="is-info" size="is-small" class="has-margin-top-10">
                <strong>SFTP vs FTP:</strong>
                <ul style="margin-top: 5px; margin-left: 15px;">
                  <li><strong>SFTP (Port 22):</strong> Encrypted, secure - <em>Recommended</em></li>
                  <li><strong>FTP (Port 21):</strong> Unencrypted, legacy only</li>
                  <li><strong>FTPS:</strong> FTP over TLS - alternative to SFTP</li>
                </ul>
              </b-message>
              <b-message type="is-warning" size="is-small" class="has-margin-top-10">
                <strong>Firewall Requirements:</strong>
                <ul style="margin-top: 5px; margin-left: 15px;">
                  <li>SFTP: TCP Port 22 (or custom SSH port)</li>
                  <li>FTP: TCP Port 21 + passive ports (e.g., 50000-50100)</li>
                  <li>Test: <code>sftp username@server</code></li>
                </ul>
              </b-message>
            </div>
          </b-collapse>

          <!-- Security Warning -->
          <b-message
            v-if="!credentialForm.is_public && ['s3', 's3_compatible', 'huawei_obs', 'wasabi', 'google', 'azure'].includes(credentialForm.storage_provider)"
            type="is-warning"
            has-icon
          >
            <strong>Security Notice:</strong>
            <ul>
              <li>Credentials are encrypted with AES-256-GCM before storage</li>
              <li>Never shared with frontend or API responses</li>
              <li>Only used to generate signed URLs (1-hour expiry)</li>
            </ul>
          </b-message>

          <!-- Network Storage Security Notice -->
          <b-message
            v-if="['smb', 'nfs', 'sftp'].includes(credentialForm.storage_provider)"
            type="is-warning"
            has-icon
          >
            <strong>Security Notice:</strong>
            <ul>
              <li>Network credentials are encrypted with AES-256-GCM before storage</li>
              <li>Use dedicated service accounts with minimum required permissions</li>
              <li>For SFTP, consider SSH key authentication for enhanced security</li>
              <li>Ensure network traffic is on a secure/internal network when possible</li>
            </ul>
          </b-message>
        </section>
        <footer class="modal-card-foot" style="justify-content: flex-end">
          <button class="button" type="button" @click="closeCredentialModal">
            Cancel
          </button>
          <button
            class="button is-info"
            type="button"
            :loading="testingCredential"
            @click="testCredentialBeforeSave"
          >
            Test Connection
          </button>
          <button
            class="button is-primary"
            :loading="savingCredential"
            @click="saveCredential"
          >
            Save
          </button>
        </footer>
      </div>
    </b-modal>

    <!-- Create/Edit Config Modal -->
    <b-modal
      :active.sync="showConfigModal"
      trap-focus
      aria-role="dialog"
      aria-modal
      has-modal-card
      @close="closeConfigModal"
    >
      <div class="modal-card" style="width: 700px">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ isEditingConfig ? 'Edit Path Configuration' : 'Add Path Configuration' }}
          </p>
        </header>
        <section class="modal-card-body">
          <b-field label="Configuration Purpose *">
            <b-select
              v-model="configForm.config_name"
              placeholder="Select purpose"
              expanded
              required
            >
              <optgroup label="Source / Input">
                <option value="source_input">Source Input (Ingest)</option>
                <option value="library">Library Storage</option>
                <option value="archive">Archive Storage</option>
                <option value="watch_folder">Watch Folder (Auto-Ingest)</option>
              </optgroup>
              <optgroup label="Processing / Work">
                <option value="work_temp">Work/Temp Storage</option>
                <option value="transcode_output">Transcode Output</option>
                <option value="stitch_output">Stitch/Conform Output</option>
                <option value="qc_output">QC Output Storage</option>
              </optgroup>
              <optgroup label="Delivery / Output">
                <option value="tx_mezzanine">TX Mezzanine Storage</option>
                <option value="vod_delivery">VOD/Catchup Delivery</option>
                <option value="linear_playout">Linear Playout</option>
                <option value="cdn_origin">CDN Origin</option>
              </optgroup>
              <optgroup label="Preview / Review">
                <option value="proxy">Proxy/Preview Storage</option>
                <option value="thumbnail">Thumbnail Storage</option>
                <option value="review_approval">Review & Approval</option>
              </optgroup>
            </b-select>
            <p class="help">Select the purpose for this storage path configuration</p>
          </b-field>

          <b-field label="Storage Credential *">
            <b-select
              v-model="configForm.storage_credential_id"
              placeholder="Select storage credential (AWS keys)"
              expanded
              required
              @input="onCredentialSelected"
            >
              <option
                v-for="cred in storageCredentials"
                :key="cred.id"
                :value="cred.id"
              >
                {{ cred.storage_name }} ({{ getProviderLabel(cred.storage_provider) }})
              </option>
            </b-select>
            <p class="help">Select the credentials to use for accessing storage</p>
          </b-field>

          <div class="columns">
            <div class="column is-8">
              <b-field label="Bucket Name *">
                <b-input
                  v-model="configForm.bucket_name"
                  placeholder="e.g., my-media-bucket"
                  required
                />
                <p class="help">Enter the bucket name to use for this path configuration</p>
              </b-field>
            </div>
            <div class="column is-4">
              <b-field label="Region">
                <b-select v-model="configForm.bucket_region" placeholder="Select region" expanded>
                  <option value="">-- Use credential default --</option>
                  <optgroup label="AWS Regions">
                    <option value="us-east-1">US East (N. Virginia)</option>
                    <option value="us-east-2">US East (Ohio)</option>
                    <option value="us-west-1">US West (N. California)</option>
                    <option value="us-west-2">US West (Oregon)</option>
                    <option value="eu-west-1">EU (Ireland)</option>
                    <option value="eu-west-2">EU (London)</option>
                    <option value="eu-central-1">EU (Frankfurt)</option>
                    <option value="af-south-1">Africa (Cape Town)</option>
                    <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                  </optgroup>
                </b-select>
              </b-field>
            </div>
          </div>

          <!-- Show selected credential info -->
          <div v-if="configForm.storage_credential_id && selectedCredentialForConfig" class="notification is-light is-small mb-4">
            <p class="is-size-7">
              <strong>Using credentials:</strong> {{ selectedCredentialForConfig.storage_name }}
              <span v-if="selectedCredentialForConfig.bucket_name">(default bucket: {{ selectedCredentialForConfig.bucket_name }})</span>
            </p>
          </div>

          <b-field label="Base Folder">
            <b-input
              v-model="configForm.base_folder"
              placeholder="e.g., archive/ or TX/"
            />
            <p class="help">Folder path within the storage bucket</p>
          </b-field>

          <b-field label="Folder Template">
            <b-input
              v-model="configForm.folder_template"
              placeholder="e.g., {media_type}/{supplier}/{year}/"
            />
            <p class="help">
              Available variables: {media_type}, {supplier}, {year}, {month}, {channel}, {title}
            </p>
          </b-field>

          <b-field label="Filename Template">
            <b-input
              v-model="configForm.filename_template"
              placeholder="e.g., TX_{id}_{title}_{resolution}.{ext}"
            />
            <p class="help">
              Available variables: {id}, {title}, {resolution}, {language}, {ext}, {date}
            </p>
          </b-field>

          <hr />
          <h4 class="title is-5">Auto-Suggest Pattern Hint</h4>
          <p class="mb-4 has-text-grey">
            Configure how Auto-Suggest matches files in this storage location to episodes.
          </p>

          <b-field label="Filename Pattern Hint">
            <b-input
              v-model="configForm.filename_pattern_hint"
              placeholder="e.g., {SHOW}_S{SS}EP{EE}_SEG_{NN}"
            />
          </b-field>

          <!-- Pattern Placeholders Reference Table -->
          <b-collapse :open="showPatternHelp" animation="slide" class="mb-4">
            <template #trigger="props">
              <a class="has-text-link" @click.prevent="showPatternHelp = !showPatternHelp">
                <b-icon :icon="props.open ? 'chevron-up' : 'chevron-down'" size="is-small"></b-icon>
                {{ props.open ? 'Hide' : 'Show' }} Pattern Placeholders Reference
              </a>
            </template>
            <div class="box mt-3" style="background: #f8f9fa;">
              <h5 class="title is-6 mb-3">Pattern Placeholders</h5>
              <table class="table is-narrow is-fullwidth is-size-7">
                <thead>
                  <tr>
                    <th>Placeholder</th>
                    <th>Meaning</th>
                    <th>Example Output</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>{SHOW}</code></td>
                    <td>Show title (alphanumeric only, spaces removed)</td>
                    <td><code>LalaNgoxolo</code>, <code>SmokeandMirrors</code></td>
                  </tr>
                  <tr>
                    <td><code>{SS}</code> or <code>{SEASON}</code></td>
                    <td>Season number with leading zero</td>
                    <td><code>01</code>, <code>02</code>, <code>03</code></td>
                  </tr>
                  <tr>
                    <td><code>{S}</code></td>
                    <td>Season number WITHOUT leading zero</td>
                    <td><code>1</code>, <code>2</code>, <code>3</code></td>
                  </tr>
                  <tr>
                    <td><code>{EE}</code> or <code>{EPISODE}</code></td>
                    <td>Episode number with leading zero</td>
                    <td><code>01</code>, <code>02</code>, <code>10</code></td>
                  </tr>
                  <tr>
                    <td><code>{E}</code></td>
                    <td>Episode number WITHOUT leading zero</td>
                    <td><code>1</code>, <code>2</code>, <code>10</code></td>
                  </tr>
                  <tr>
                    <td><code>{NN}</code> or <code>{SEG}</code></td>
                    <td>Segment number (ignored in search, used for display)</td>
                    <td><code>01</code>, <code>02</code>, <code>03</code></td>
                  </tr>
                </tbody>
              </table>
              <h5 class="title is-6 mb-2 mt-4">Example Patterns</h5>
              <ul class="is-size-7">
                <li><code>MJT_EP_{SHOW}_S{SS}EP{EE}_SEG_{NN}</code> → <code>MJT_EP_LalaNgoxolo_S03EP02_SEG_01</code></li>
                <li><code>TX_{SHOW}_S{S}E{E}</code> → <code>TX_LalaNgoxolo_S3E2</code></li>
                <li><code>{SHOW}_Season{SS}_Ep{EE}</code> → <code>LalaNgoxolo_Season03_Ep02</code></li>
              </ul>
              <p class="is-size-7 has-text-grey mt-3">
                <b-icon icon="information" size="is-small"></b-icon>
                Include any hardcoded text (like <code>MJT_EP_</code> or <code>TX_</code>) exactly as it appears in your filenames.
              </p>
            </div>
          </b-collapse>

          <b-field label="Sample Filenames (for reference)">
            <b-input
              v-model="configForm.sample_filenames"
              type="textarea"
              placeholder="e.g., LalaNgoxolo_S03EP02_SEG_01_08M16.mxf"
              rows="2"
            />
            <p class="help">
              Paste some sample filenames from this storage to help configure the pattern
            </p>
          </b-field>

          <hr />
          <h4 class="title is-5">Default Encoding Settings</h4>

          <div class="columns">
            <div class="column">
              <b-field label="Default Codec">
                <b-select v-model="configForm.default_codec" placeholder="Select codec" expanded>
                  <optgroup label="Standard Codecs">
                    <option value="h264">H.264 (AVC)</option>
                    <option value="h265">H.265 (HEVC)</option>
                    <option value="prores">Apple ProRes</option>
                  </optgroup>
                  <optgroup label="Broadcast Codecs">
                    <option value="xdcam_50i">XDCAM HD 50i (50 Mbps)</option>
                    <option value="xdcam_35">XDCAM EX 35</option>
                    <option value="dnxhd">Avid DNxHD</option>
                    <option value="imx50">Sony IMX 50</option>
                  </optgroup>
                </b-select>
              </b-field>
            </div>
            <div class="column">
              <b-field label="Default Container">
                <b-select v-model="configForm.default_container" placeholder="Select container" expanded>
                  <option value="mp4">MP4</option>
                  <option value="mxf">MXF</option>
                  <option value="mov">QuickTime (MOV)</option>
                  <option value="ts">MPEG-TS</option>
                </b-select>
              </b-field>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <b-field label="Default Bitrate (kbps)">
                <b-input
                  v-model.number="configForm.default_bitrate_kbps"
                  type="number"
                  placeholder="e.g., 20000 for 20mbps"
                />
              </b-field>
            </div>
            <div class="column">
              <b-field label="Default Resolution">
                <b-select v-model="configForm.default_resolution" placeholder="Select resolution" expanded>
                  <option value="1920x1080">1920x1080 (Full HD)</option>
                  <option value="1280x720">1280x720 (HD)</option>
                  <option value="3840x2160">3840x2160 (4K UHD)</option>
                  <option value="720x576">720x576 (SD PAL)</option>
                </b-select>
              </b-field>
            </div>
          </div>

          <b-field label="Default Framerate">
            <b-select v-model="configForm.default_framerate" placeholder="Select framerate" expanded>
              <option value="25">25 fps (PAL)</option>
              <option value="50i">50i (PAL Interlaced)</option>
              <option value="29.97">29.97 fps (NTSC)</option>
              <option value="59.94i">59.94i (NTSC Interlaced)</option>
              <option value="24">24 fps (Film)</option>
              <option value="23.976">23.976 fps (Film Pulldown)</option>
            </b-select>
          </b-field>

          <!-- Preset Buttons -->
          <div class="buttons has-margin-top-15">
            <b-button size="is-small" @click="applyPreset('tx_archive')">
              TX/Archive Preset (H264 20mbps)
            </b-button>
            <b-button size="is-small" @click="applyPreset('vod_xdcam')">
              VOD Preset (XDCAM 50i)
            </b-button>
            <b-button size="is-small" @click="applyPreset('proxy')">
              Proxy Preset (H264 2mbps)
            </b-button>
          </div>

          <hr />
          <h4 class="title is-5">Encoding Preset Link</h4>
          <p class="subtitle is-6">Link this storage path to a predefined encoding preset for processing jobs</p>

          <b-field label="Encoding Preset">
            <b-select v-model="configForm.encoding_preset_id" placeholder="Select encoding preset (optional)" expanded>
              <option :value="null">-- No preset linked --</option>
              <option
                v-for="preset in encodingPresets"
                :key="preset.id"
                :value="preset.id"
              >
                {{ preset.preset_name }}
                <template v-if="preset.is_default">(Default)</template>
                - {{ getCodecLabel(preset.video_codec) }} @ {{ formatBitrate(preset.video_bitrate_kbps) }}
              </option>
            </b-select>
            <p class="help">When linked, processing jobs for files in this path will use the encoding preset settings</p>
          </b-field>

          <!-- Processing Location - SUPER ADMIN ONLY -->
          <div v-if="isSuperAdmin">
            <hr />
            <h4 class="title is-5">
              Processing Location
              <b-tag type="is-warning" size="is-small" class="ml-2">Super Admin Only</b-tag>
            </h4>
            <p class="subtitle is-6">Configure where media processing occurs (affects billing)</p>

            <b-field label="Processing Location">
              <b-select v-model="configForm.processing_location" placeholder="Select processing location" expanded>
                <option :value="null">-- Not Configured --</option>
                <option value="bms_mediaconvert">AWS MediaConvert (BMS Account) - Requires S3 source</option>
                <option value="bms_ffmpeg">FFmpeg (BMS EC2/Fargate) - Works with any source</option>
                <option value="client_aws">Client AWS Account - Cross-account processing (BYOC)</option>
                <option value="local">Local/On-Premise - Client infrastructure</option>
              </b-select>
            </b-field>

            <!-- BMS FFmpeg Options -->
            <div v-if="configForm.processing_location === 'bms_ffmpeg'" class="box has-background-light">
              <h5 class="title is-6">FFmpeg Processing Settings</h5>
              <b-field label="FFmpeg Preset">
                <b-select v-model="configForm.ffmpeg_preset" placeholder="Select FFmpeg preset" expanded>
                  <optgroup label="TX/Archive">
                    <option value="tx_h264_20mbps">TX/Archive H.264 20Mbps</option>
                    <option value="tx_h264_50mbps">TX/Archive H.264 50Mbps</option>
                  </optgroup>
                  <optgroup label="VOD">
                    <option value="vod_xdcam_50i">VOD XDCAM 50i</option>
                    <option value="vod_h264_8mbps">VOD H.264 8Mbps</option>
                  </optgroup>
                  <optgroup label="Proxy">
                    <option value="proxy_h264_2mbps">Proxy H.264 2Mbps</option>
                    <option value="proxy_h264_1mbps">Proxy H.264 1Mbps</option>
                  </optgroup>
                  <optgroup label="Professional">
                    <option value="prores_422">ProRes 422</option>
                    <option value="prores_422hq">ProRes 422 HQ</option>
                    <option value="dnxhd_185">DNxHD 185</option>
                  </optgroup>
                </b-select>
              </b-field>
              <b-message type="is-info" has-icon size="is-small">
                FFmpeg can process files from any source: S3, HTTP URLs, RTMP streams, or 3rd party storage (Wasabi, Backblaze, etc.)
              </b-message>
            </div>

            <!-- BMS MediaConvert Options -->
            <div v-if="configForm.processing_location === 'bms_mediaconvert'" class="box has-background-light">
              <h5 class="title is-6">AWS MediaConvert Settings</h5>
              <b-field label="MediaConvert Preset ARN (Optional)">
                <b-input
                  v-model="configForm.mediaconvert_preset_arn"
                  placeholder="arn:aws:mediaconvert:region:account:presets/preset-name"
                />
              </b-field>
              <b-field label="MediaConvert Queue ARN (Optional)">
                <b-input
                  v-model="configForm.mediaconvert_queue_arn"
                  placeholder="arn:aws:mediaconvert:region:account:queues/queue-name"
                />
              </b-field>
              <b-message type="is-warning" has-icon size="is-small">
                <strong>Note:</strong> MediaConvert requires source files to be in AWS S3. Files from other storage providers will be copied to S3 first.
              </b-message>
            </div>

            <!-- Client AWS Options -->
            <div v-if="configForm.processing_location === 'client_aws'" class="box has-background-light">
              <h5 class="title is-6">Client AWS Account Settings (BYOC)</h5>
              <b-field label="Client AWS Role ARN *">
                <b-input
                  v-model="configForm.client_aws_role_arn"
                  placeholder="arn:aws:iam::123456789012:role/BMSProcessingRole"
                  required
                />
                <p class="help">IAM role in client's AWS account that BMS can assume for cross-account processing</p>
              </b-field>
              <b-field label="External ID (Recommended)">
                <b-input
                  v-model="configForm.client_aws_external_id"
                  placeholder="bms-external-id-abc123"
                />
                <p class="help">External ID for additional security when assuming cross-account role</p>
              </b-field>
              <b-field label="Client AWS Region">
                <b-select v-model="configForm.client_aws_region" placeholder="Select region" expanded>
                  <option value="us-east-1">US East (N. Virginia)</option>
                  <option value="us-east-2">US East (Ohio)</option>
                  <option value="us-west-1">US West (N. California)</option>
                  <option value="us-west-2">US West (Oregon)</option>
                  <option value="eu-west-1">EU (Ireland)</option>
                  <option value="eu-west-2">EU (London)</option>
                  <option value="eu-central-1">EU (Frankfurt)</option>
                  <option value="af-south-1">Africa (Cape Town)</option>
                  <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                </b-select>
              </b-field>
              <b-message type="is-info" has-icon size="is-small">
                <strong>BYOC Model:</strong> Processing happens in the client's AWS account. BMS assumes the specified IAM role to submit MediaConvert jobs and manage files in client's S3.
              </b-message>
            </div>

            <!-- Local Processing Options -->
            <div v-if="configForm.processing_location === 'local'" class="box has-background-light">
              <h5 class="title is-6">Local/On-Premise Processing</h5>
              <b-message type="is-info" has-icon size="is-small">
                BMS will push processing jobs to the client's local job queue. Requires BMS Agent installed on client's infrastructure.
              </b-message>
            </div>

            <!-- Processing Options -->
            <div v-if="configForm.processing_location" class="has-margin-top-15">
              <h5 class="title is-6">Processing Options</h5>
              <div class="columns">
                <div class="column">
                  <b-field label="Processing Priority">
                    <b-select v-model="configForm.processing_priority" expanded>
                      <option value="low">Low - Background processing</option>
                      <option value="normal">Normal - Standard queue</option>
                      <option value="high">High - Priority processing</option>
                      <option value="urgent">Urgent - Immediate (extra cost)</option>
                    </b-select>
                  </b-field>
                </div>
                <div class="column">
                  <b-field>
                    <b-checkbox v-model="configForm.enable_qc_after_processing">
                      Run QC after processing completes
                    </b-checkbox>
                  </b-field>
                  <b-field>
                    <b-checkbox v-model="configForm.auto_deliver_after_processing">
                      Auto-deliver to destination after processing
                    </b-checkbox>
                  </b-field>
                </div>
              </div>
            </div>
          </div>

          <!-- Gateway Settings Section (VersityGW / SMB Path Mapping) -->
          <hr />
          <h4 class="title is-5">
            Gateway Settings
            <b-tag type="is-info" size="is-small" class="ml-2">Optional</b-tag>
          </h4>
          <p class="subtitle is-6">Configure S3-to-SMB gateway for local playout systems (VersityGW, rclone, etc.)</p>

          <b-field label="Gateway Type">
            <b-select v-model="configForm.gateway_type" placeholder="Select gateway type" expanded>
              <option value="none">None (Direct S3 Access)</option>
              <option value="versitygw">VersityGW - S3-to-filesystem gateway</option>
              <option value="rclone">rclone Mount - S3 as local filesystem</option>
              <option value="minio_gateway">MinIO Gateway - S3 compatibility</option>
              <option value="other">Other Gateway</option>
            </b-select>
            <p class="help">Select the type of gateway used to access S3 storage as local filesystem</p>
          </b-field>

          <!-- Gateway-specific settings -->
          <div v-if="configForm.gateway_type && configForm.gateway_type !== 'none'" class="box has-background-light">
            <h5 class="title is-6">{{ getGatewayLabel(configForm.gateway_type) }} Settings</h5>

            <b-field label="Gateway Endpoint URL">
              <b-input
                v-model="configForm.gateway_endpoint"
                placeholder="e.g., http://localhost:7070 or http://versity.local:7070"
              />
              <p class="help">URL where the gateway service is running (for API access)</p>
            </b-field>

            <b-field>
              <b-checkbox v-model="configForm.smb_path_mapping_enabled">
                Enable SMB Path Mapping for Playout Exports
              </b-checkbox>
              <p class="help">When enabled, playout exports (MCRList, BXF) can use this SMB path instead of channel config</p>
            </b-field>

            <b-field v-if="configForm.smb_path_mapping_enabled" label="SMB Share Path *">
              <b-input
                v-model="configForm.smb_share_path"
                placeholder="e.g., \\\\server\\share\\media or Z:\\Media"
              />
              <p class="help">UNC path or mapped drive where playout system accesses files (e.g., \\192.168.1.100\playout)</p>
            </b-field>

            <div class="columns">
              <div class="column">
                <b-field label="Cache Policy">
                  <b-select v-model="configForm.cache_policy" expanded>
                    <option value="none">No Caching</option>
                    <option value="lru">LRU (Least Recently Used)</option>
                    <option value="schedule_aware">Schedule-Aware Pre-caching</option>
                    <option value="always_cache">Always Cache</option>
                  </b-select>
                </b-field>
              </div>
              <div class="column">
                <b-field label="Cache Size (GB)">
                  <b-input
                    v-model.number="configForm.cache_size_gb"
                    type="number"
                    placeholder="e.g., 500"
                  />
                </b-field>
              </div>
            </div>

            <b-field v-if="configForm.cache_policy === 'schedule_aware'" label="Pre-cache Days Ahead">
              <b-input
                v-model.number="configForm.cache_days_ahead"
                type="number"
                placeholder="e.g., 7"
              />
              <p class="help">Number of days ahead to pre-cache scheduled content</p>
            </b-field>
          </div>

          <!-- Lifecycle Management Section -->
          <hr />
          <h4 class="title is-5">
            Lifecycle Management
            <b-tag type="is-info" size="is-small" class="ml-2">S3 Storage Tiers</b-tag>
          </h4>
          <p class="subtitle is-6">Automatically manage file storage classes based on broadcast schedules</p>

          <b-field>
            <b-checkbox v-model="configForm.lifecycle_enabled">
              Enable Automatic Lifecycle Management
            </b-checkbox>
            <p class="help">Auto-restore archived files before schedule, auto-archive after last airdate</p>
          </b-field>

          <div v-if="configForm.lifecycle_enabled" class="box has-background-light">
            <h5 class="title is-6">Restore Settings</h5>
            <div class="columns">
              <div class="column">
                <b-field label="Restore Days Before Air">
                  <b-input
                    v-model.number="configForm.restore_days_before_air"
                    type="number"
                    placeholder="e.g., 7"
                  />
                  <p class="help">Days before scheduled airtime to restore from archive</p>
                </b-field>
              </div>
              <div class="column">
                <b-field label="Restore Tier">
                  <b-select v-model="configForm.restore_tier" expanded>
                    <option value="bulk">Bulk (5-12 hours) - Cheapest</option>
                    <option value="standard">Standard (3-5 hours)</option>
                    <option value="expedited">Expedited (1-5 minutes) - Fastest</option>
                  </b-select>
                </b-field>
              </div>
            </div>

            <h5 class="title is-6 mt-4">Archive Settings</h5>
            <div class="columns">
              <div class="column">
                <b-field label="Archive Days After Last Air">
                  <b-input
                    v-model.number="configForm.archive_days_after_last_air"
                    type="number"
                    placeholder="e.g., 30"
                  />
                  <p class="help">Days after last scheduled airtime to archive files</p>
                </b-field>
              </div>
              <div class="column">
                <b-field label="Archive Storage Class">
                  <b-select v-model="configForm.storage_class_archive" expanded>
                    <option value="GLACIER_IR">Glacier Instant Retrieval</option>
                    <option value="GLACIER">Glacier Flexible Retrieval</option>
                    <option value="DEEP_ARCHIVE">Glacier Deep Archive (Cheapest)</option>
                  </b-select>
                </b-field>
              </div>
            </div>

            <h5 class="title is-6 mt-4">Storage Class Mapping</h5>
            <p class="has-text-grey is-size-7 mb-3">Define which S3 storage class to use for each tier</p>
            <div class="columns">
              <div class="column">
                <b-field label="Hot Tier (Active Content)">
                  <b-select v-model="configForm.storage_class_hot" expanded>
                    <option value="STANDARD">Standard</option>
                    <option value="INTELLIGENT_TIERING">Intelligent Tiering</option>
                  </b-select>
                </b-field>
              </div>
              <div class="column">
                <b-field label="Warm Tier (Infrequent)">
                  <b-select v-model="configForm.storage_class_warm" expanded>
                    <option value="STANDARD_IA">Standard-IA</option>
                    <option value="ONEZONE_IA">One Zone-IA</option>
                    <option value="INTELLIGENT_TIERING">Intelligent Tiering</option>
                  </b-select>
                </b-field>
              </div>
            </div>

            <b-message type="is-info" has-icon size="is-small" class="mt-4">
              <strong>How it works:</strong> Files scheduled for broadcast will be automatically restored from archive
              {{ configForm.restore_days_before_air || 7 }} days before airtime using {{ configForm.restore_tier || 'standard' }} tier.
              After the last scheduled airdate plus {{ configForm.archive_days_after_last_air || 30 }} days,
              files will be moved to {{ configForm.storage_class_archive || 'GLACIER' }}.
            </b-message>
          </div>
        </section>
        <footer class="modal-card-foot" style="justify-content: flex-end">
          <button class="button" type="button" @click="closeConfigModal">
            Cancel
          </button>
          <button
            class="button is-primary"
            :loading="savingConfig"
            @click="saveConfig"
          >
            Save
          </button>
        </footer>
      </div>
    </b-modal>

    <!-- Delete Confirmation Modal -->
    <b-modal
      :active.sync="showDeleteModal"
      trap-focus
      aria-role="dialog"
      aria-modal
      has-modal-card
      @close="showDeleteModal = false"
    >
      <div class="modal-card" style="width: auto">
        <header class="modal-card-head">
          <p class="modal-card-title">Confirm Delete?</p>
        </header>
        <section class="modal-card-body">
          <p>Are you sure you want to delete this {{ deleteType }}?</p>
          <p v-if="selectedItem" class="has-text-weight-bold">
            {{ selectedItem.storage_name || selectedItem.config_name || selectedItem.preset_name || selectedItem.workflow_name }}
          </p>
          <p class="has-text-danger">This action cannot be undone.</p>
        </section>
        <footer class="modal-card-foot" style="justify-content: flex-end">
          <button class="button" type="button" @click="showDeleteModal = false">
            Cancel
          </button>
          <button class="button is-danger" @click="executeDelete">
            Delete
          </button>
        </footer>
      </div>
    </b-modal>

    <!-- Create/Edit Processing Workflow Modal -->
    <b-modal
      :active.sync="showWorkflowModal"
      trap-focus
      aria-role="dialog"
      aria-modal
      has-modal-card
      @close="closeWorkflowModal"
    >
      <div class="modal-card" style="width: 700px; max-height: 90vh;">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ isEditingWorkflow ? 'Edit Workflow' : 'Add Workflow' }}
          </p>
        </header>
        <section class="modal-card-body" style="overflow-y: auto;">
          <!-- Basic Info -->
          <div class="columns">
            <div class="column">
              <b-field label="Workflow Name *">
                <b-input
                  v-model="workflowForm.workflow_name"
                  placeholder="e.g., TX Master Output"
                  required
                />
              </b-field>
            </div>
            <div class="column">
              <b-field label="Workflow Code">
                <b-input
                  v-model="workflowForm.workflow_code"
                  placeholder="e.g., tx_master (optional)"
                />
              </b-field>
            </div>
          </div>

          <b-field label="Description">
            <b-input
              v-model="workflowForm.description"
              type="textarea"
              placeholder="Describe what this workflow does..."
              rows="2"
            />
          </b-field>

          <hr />

          <!-- Platform Workflow Template -->
          <b-field label="Platform Workflow Template">
            <b-select
              v-model="workflowForm.workflow_template_id"
              placeholder="Select a platform workflow template"
              expanded
              :disabled="platformWorkflows.length === 0"
            >
              <option :value="null">-- None (Custom Workflow) --</option>
              <option
                v-for="template in platformWorkflows"
                :key="template.id"
                :value="template.id"
              >
                {{ template.template_name }}{{ template.workflow_category ? ' (' + getWorkflowCategoryLabel(template.workflow_category) + ')' : '' }}
              </option>
            </b-select>
            <p v-if="platformWorkflows.length > 0" class="help">
              Select a platform template to use pre-configured processing settings from your organization's approved workflows.
            </p>
            <p v-else class="help has-text-warning">
              No platform workflow templates available. Contact your administrator to grant access to workflow templates.
            </p>
          </b-field>

          <!-- Workflow Type (only shown when no platform template selected) -->
          <b-field v-if="!workflowForm.workflow_template_id" label="Workflow Type">
            <b-select v-model="workflowForm.workflow_type" expanded>
              <option value="transcode">Transcode - Re-encode to different format/codec</option>
              <option value="qc_only">QC Only - Quality check without re-encoding</option>
              <option value="copy">Copy - Transfer files between storage locations</option>
              <option value="archive">Archive - Prepare for long-term/cold storage</option>
            </b-select>
          </b-field>

          <div v-if="workflowForm.workflow_template_id" class="notification is-success is-light">
            <p class="is-size-7">
              <b-icon icon="check-circle" size="is-small"></b-icon>
              <strong>Platform Template Selected:</strong> This workflow will use the processing settings defined in the platform template.
              Your organization's QC thresholds and loudness settings will be applied automatically.
            </p>
          </div>

          <div v-else class="notification is-info is-light">
            <p class="is-size-7">
              <strong>Note:</strong> For episodic content, stitch vs encode is auto-determined based on single file or multi-segment selection.
              Use the <em>Run QC on input file</em> toggle in the encoding preset to control whether QC analysis runs on the source file before transcoding.
            </p>
          </div>

          <hr />

          <!-- Storage Configuration -->
          <h4 class="title is-5">Storage Configuration</h4>

          <div class="columns">
            <div class="column">
              <b-field label="Input Storage">
                <b-select v-model="workflowForm.input_storage_config_id" placeholder="Select input source" expanded>
                  <option :value="null">-- Not Set --</option>
                  <option
                    v-for="config in storageConfigs"
                    :key="config.id"
                    :value="config.id"
                  >
                    {{ formatConfigName(config.config_name) }}
                    <template v-if="config.bucket_name"> - {{ config.bucket_name }}</template>
                  </option>
                </b-select>
              </b-field>
            </div>
            <div class="column">
              <b-field label="Output Storage">
                <b-select v-model="workflowForm.output_storage_config_id" placeholder="Select output destination" expanded>
                  <option :value="null">-- Not Set --</option>
                  <option
                    v-for="config in storageConfigs"
                    :key="config.id"
                    :value="config.id"
                  >
                    {{ formatConfigName(config.config_name) }}
                    <template v-if="config.bucket_name"> - {{ config.bucket_name }}</template>
                  </option>
                </b-select>
              </b-field>
            </div>
          </div>

          <b-field label="Encoding Preset">
            <b-select v-model="workflowForm.encoding_preset_id" placeholder="Select encoding preset" expanded>
              <option :value="null">-- Use Default --</option>
              <option
                v-for="preset in encodingPresets"
                :key="preset.id"
                :value="preset.id"
              >
                {{ preset.preset_name }}
                <template v-if="preset.is_default">(Default)</template>
              </option>
            </b-select>
          </b-field>

          <hr />

          <!-- Workflow Settings -->
          <h4 class="title is-5">Workflow Settings</h4>

          <div class="columns">
            <div class="column">
              <b-field label="Priority">
                <b-select v-model="workflowForm.priority" expanded>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </b-select>
              </b-field>
            </div>
            <div class="column">
              <b-field label="Max Concurrent Jobs">
                <b-input
                  v-model.number="workflowForm.max_concurrent_jobs"
                  type="number"
                  min="1"
                  max="20"
                />
              </b-field>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <b-field>
                <b-checkbox v-model="workflowForm.auto_qc_after_process">
                  Run QC after processing
                </b-checkbox>
              </b-field>
              <b-field>
                <b-checkbox v-model="workflowForm.auto_deliver_after_qc">
                  Auto-deliver after QC passes
                </b-checkbox>
              </b-field>
            </div>
            <div class="column">
              <b-field>
                <b-checkbox v-model="workflowForm.notify_on_complete">
                  Notify on completion
                </b-checkbox>
              </b-field>
              <b-field>
                <b-checkbox v-model="workflowForm.notify_on_error">
                  Notify on error
                </b-checkbox>
              </b-field>
            </div>
          </div>

          <b-field>
            <b-checkbox v-model="workflowForm.is_default">
              Set as default workflow for this organisation
            </b-checkbox>
          </b-field>
        </section>
        <footer class="modal-card-foot" style="justify-content: flex-end">
          <button class="button" type="button" @click="closeWorkflowModal">
            Cancel
          </button>
          <button
            class="button is-primary"
            :class="{ 'is-loading': savingWorkflowForm }"
            @click="saveProcessingWorkflow"
          >
            Save
          </button>
        </footer>
      </div>
    </b-modal>

    <!-- Create/Edit Encoding Preset Modal -->
    <b-modal
      :active.sync="showPresetModal"
      trap-focus
      aria-role="dialog"
      aria-modal
      has-modal-card
      @close="closePresetModal"
    >
      <div class="modal-card" style="width: 800px; max-height: 90vh;">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ isEditingPreset ? 'Edit Encoding Preset' : 'Add Encoding Preset' }}
          </p>
        </header>
        <section class="modal-card-body" style="overflow-y: auto;">
          <!-- Basic Info -->
          <div class="columns">
            <div class="column">
              <b-field label="Preset Name *">
                <b-input
                  v-model="presetForm.preset_name"
                  placeholder="e.g., TX Master 1080i"
                  required
                />
              </b-field>
            </div>
            <div class="column">
              <b-field label="Preset Code">
                <b-input
                  v-model="presetForm.preset_code"
                  placeholder="e.g., tx_master_1080i (auto-generated if empty)"
                />
              </b-field>
            </div>
          </div>

          <b-field label="Description">
            <b-input
              v-model="presetForm.description"
              type="textarea"
              placeholder="Describe the use case for this preset..."
              rows="2"
            />
          </b-field>

          <!-- Quick Presets -->
          <div class="box has-background-light">
            <h5 class="title is-6">Quick Start Presets</h5>
            <div class="buttons">
              <b-button size="is-small" @click="applyQuickPreset('tx_master')">
                TX Master 1080i
              </b-button>
              <b-button size="is-small" @click="applyQuickPreset('vod_hd')">
                VOD HD 8Mbps
              </b-button>
              <b-button size="is-small" @click="applyQuickPreset('proxy')">
                Proxy 2Mbps
              </b-button>
              <b-button size="is-small" @click="applyQuickPreset('archive')">
                Archive 50Mbps
              </b-button>
            </div>
          </div>

          <hr />

          <!-- Video Settings -->
          <h4 class="title is-5">Video Settings</h4>
          <div class="columns">
            <div class="column">
              <b-field label="Video Codec *">
                <b-select v-model="presetForm.video_codec" expanded required>
                  <optgroup label="Standard">
                    <option value="h264">H.264 (AVC)</option>
                    <option value="h265">H.265 (HEVC)</option>
                  </optgroup>
                  <optgroup label="Professional">
                    <option value="prores_422">ProRes 422</option>
                    <option value="prores_422hq">ProRes 422 HQ</option>
                    <option value="prores_4444">ProRes 4444</option>
                    <option value="dnxhd_185">DNxHD 185</option>
                    <option value="dnxhd_120">DNxHD 120</option>
                    <option value="dnxhr_hq">DNxHR HQ</option>
                  </optgroup>
                  <optgroup label="Broadcast">
                    <option value="xdcam_hd">XDCAM HD</option>
                    <option value="xdcam_50">XDCAM HD 50</option>
                    <option value="mpeg2">MPEG-2</option>
                  </optgroup>
                </b-select>
              </b-field>
            </div>
            <div class="column">
              <b-field label="Bitrate (kbps) *">
                <b-input
                  v-model.number="presetForm.video_bitrate_kbps"
                  type="number"
                  placeholder="17500"
                  required
                />
                <p class="help">{{ formatBitrate(presetForm.video_bitrate_kbps) }}</p>
              </b-field>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <b-field label="Max Bitrate (kbps)">
                <b-input
                  v-model.number="presetForm.video_maxrate_kbps"
                  type="number"
                  placeholder="Optional - for VBR encoding"
                />
              </b-field>
            </div>
            <div class="column">
              <b-field label="Buffer Size (kbps)">
                <b-input
                  v-model.number="presetForm.video_bufsize_kbps"
                  type="number"
                  placeholder="Optional"
                />
              </b-field>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <b-field label="Profile">
                <b-select v-model="presetForm.video_profile" expanded>
                  <option value="baseline">Baseline</option>
                  <option value="main">Main</option>
                  <option value="high">High</option>
                  <option value="high10">High 10</option>
                  <option value="high422">High 4:2:2</option>
                </b-select>
              </b-field>
            </div>
            <div class="column">
              <b-field label="Level">
                <b-select v-model="presetForm.video_level" expanded>
                  <option value="3.0">3.0</option>
                  <option value="3.1">3.1</option>
                  <option value="4.0">4.0</option>
                  <option value="4.1">4.1</option>
                  <option value="4.2">4.2</option>
                  <option value="5.0">5.0</option>
                  <option value="5.1">5.1</option>
                </b-select>
              </b-field>
            </div>
          </div>

          <hr />

          <!-- Resolution & Frame Rate -->
          <h4 class="title is-5">Resolution & Frame Rate</h4>
          <div class="columns">
            <div class="column">
              <b-field label="Width *">
                <b-input v-model.number="presetForm.width" type="number" placeholder="1920" required />
              </b-field>
            </div>
            <div class="column">
              <b-field label="Height *">
                <b-input v-model.number="presetForm.height" type="number" placeholder="1080" required />
              </b-field>
            </div>
            <div class="column">
              <b-field label="Frame Rate *">
                <b-select v-model="presetForm.framerate" expanded required>
                  <option value="25">25 fps (PAL)</option>
                  <option value="50i">50i (PAL Interlaced)</option>
                  <option value="50">50 fps (PAL HFR)</option>
                  <option value="29.97">29.97 fps (NTSC)</option>
                  <option value="59.94i">59.94i (NTSC Interlaced)</option>
                  <option value="23.976">23.976 fps (Film)</option>
                  <option value="24">24 fps (Film)</option>
                </b-select>
              </b-field>
            </div>
          </div>

          <div class="columns">
            <div class="column is-4">
              <b-field>
                <b-checkbox v-model="presetForm.interlaced">
                  Interlaced Output
                </b-checkbox>
              </b-field>
            </div>
            <div class="column is-4">
              <b-field label="Field Order" v-if="presetForm.interlaced">
                <b-select v-model="presetForm.field_order" expanded>
                  <option value="tff">Top Field First (TFF)</option>
                  <option value="bff">Bottom Field First (BFF)</option>
                </b-select>
              </b-field>
            </div>
            <div class="column is-4">
              <b-field label="Pixel Format">
                <b-select v-model="presetForm.pixel_format" expanded>
                  <option value="yuv420p">YUV 4:2:0 (yuv420p)</option>
                  <option value="yuv422p">YUV 4:2:2 (yuv422p)</option>
                  <option value="yuv420p10le">YUV 4:2:0 10-bit</option>
                  <option value="yuv422p10le">YUV 4:2:2 10-bit</option>
                </b-select>
              </b-field>
            </div>
          </div>

          <hr />

          <!-- Audio Settings -->
          <h4 class="title is-5">Audio Settings</h4>
          <div class="columns">
            <div class="column">
              <b-field label="Audio Codec *">
                <b-select v-model="presetForm.audio_codec" expanded required>
                  <option value="aac">AAC</option>
                  <option value="pcm_s16le">PCM 16-bit</option>
                  <option value="pcm_s24le">PCM 24-bit</option>
                  <option value="ac3">AC-3 (Dolby Digital)</option>
                  <option value="eac3">E-AC-3 (Dolby Digital Plus)</option>
                  <option value="mp2">MP2</option>
                </b-select>
              </b-field>
            </div>
            <div class="column">
              <b-field label="Bitrate (kbps) *">
                <b-input
                  v-model.number="presetForm.audio_bitrate_kbps"
                  type="number"
                  placeholder="192"
                  required
                />
              </b-field>
            </div>
            <div class="column">
              <b-field label="Channels">
                <b-select v-model.number="presetForm.audio_channels" expanded>
                  <option :value="1">Mono (1)</option>
                  <option :value="2">Stereo (2)</option>
                  <option :value="6">5.1 Surround (6)</option>
                  <option :value="8">7.1 Surround (8)</option>
                </b-select>
              </b-field>
            </div>
            <div class="column">
              <b-field label="Sample Rate">
                <b-select v-model.number="presetForm.audio_sample_rate" expanded>
                  <option :value="44100">44.1 kHz</option>
                  <option :value="48000">48 kHz</option>
                  <option :value="96000">96 kHz</option>
                </b-select>
              </b-field>
            </div>
          </div>

          <hr />

          <!-- Loudness Settings -->
          <h4 class="title is-5">Loudness Normalization (EBU R128)</h4>
          <b-field>
            <b-checkbox v-model="presetForm.apply_loudness_normalization">
              Apply Loudness Normalization
            </b-checkbox>
          </b-field>

          <div v-if="presetForm.apply_loudness_normalization" class="columns">
            <div class="column">
              <b-field label="Target Loudness (LUFS)">
                <b-input
                  v-model.number="presetForm.loudness_target_lufs"
                  type="number"
                  step="0.1"
                  placeholder="-23.0"
                />
                <p class="help">EBU R128: -23 LUFS, ATSC A/85: -24 LUFS</p>
              </b-field>
            </div>
            <div class="column">
              <b-field label="True Peak (dBTP)">
                <b-input
                  v-model.number="presetForm.loudness_true_peak_dbtp"
                  type="number"
                  step="0.1"
                  placeholder="-3.0"
                />
                <p class="help">EBU R128: -3 dBTP max</p>
              </b-field>
            </div>
            <div class="column">
              <b-field label="Loudness Range (LU)">
                <b-input
                  v-model.number="presetForm.loudness_range_lu"
                  type="number"
                  step="0.1"
                  placeholder="11.0"
                />
              </b-field>
            </div>
          </div>

          <div class="buttons has-margin-top-15">
            <b-button size="is-small" @click="applyLoudnessStandard('ebu')">
              EBU R128 (Europe)
            </b-button>
            <b-button size="is-small" @click="applyLoudnessStandard('atsc')">
              ATSC A/85 (North America)
            </b-button>
            <b-button size="is-small" @click="applyLoudnessStandard('op59')">
              OP-59 (Australia)
            </b-button>
          </div>

          <hr />

          <!-- Output Settings -->
          <h4 class="title is-5">Output Settings</h4>
          <div class="columns">
            <div class="column">
              <b-field label="Container Format *">
                <b-select v-model="presetForm.container_format" expanded required>
                  <option value="mp4">MP4</option>
                  <option value="mxf">MXF</option>
                  <option value="mov">QuickTime (MOV)</option>
                  <option value="ts">MPEG-TS</option>
                  <option value="mkv">Matroska (MKV)</option>
                </b-select>
              </b-field>
            </div>
            <div class="column">
              <b-field>
                <b-checkbox v-model="presetForm.faststart">
                  Enable Faststart (move moov atom for streaming)
                </b-checkbox>
              </b-field>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <b-field>
                <b-checkbox v-model="presetForm.enable_qc_after_encode">
                  Run QC on input file
                </b-checkbox>
              </b-field>
            </div>
            <div class="column">
              <b-field>
                <b-checkbox v-model="presetForm.preserve_timecode">
                  Preserve source timecode
                </b-checkbox>
              </b-field>
            </div>
            <div class="column">
              <b-field>
                <b-checkbox v-model="presetForm.burn_in_timecode">
                  Burn-in timecode (for proxy/review)
                </b-checkbox>
              </b-field>
            </div>
          </div>

          <b-field>
            <b-checkbox v-model="presetForm.is_default">
              Set as default preset for this organization
            </b-checkbox>
          </b-field>
        </section>
        <footer class="modal-card-foot" style="justify-content: flex-end">
          <button class="button" type="button" @click="closePresetModal">
            Cancel
          </button>
          <button
            class="button is-primary"
            :loading="savingPreset"
            @click="savePreset"
          >
            Save Preset
          </button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import CardComponent from '@/components/CardComponent'
import StorageCredentialsApi from '@/api/storageCredentials'
import StorageConfigApi from '@/api/storageConfig'
import EncodingPresetsApi from '@/api/encodingPresets'
import SpotProcessingApi from '@/api/spotProcessing'
import ProcessingWorkflowsApi from '@/api/processingWorkflows'
import WorkflowTemplatesApi from '@/api/workflowTemplates'
import S3UploadsApi from '@/api/s3Uploads'
import { mapGetters } from 'vuex'

export default {
  name: 'ManageStorage',
  components: {
    CardComponent
  },
  metaInfo: {
    title: 'Manage Storage'
  },
  computed: {
    ...mapGetters(['user']),
    isSuperAdmin() {
      return this.user && this.user.role && this.user.role.includes('superadmin')
    },
    // Filter storage configs suitable for input sources
    inputStorageOptions() {
      const inputPurposes = ['source_input', 'library', 'archive', 'watch_folder']
      return this.storageConfigs.filter(c =>
        inputPurposes.includes(c.config_name) && c.is_active
      )
    },
    // Filter storage configs suitable for output destinations
    outputStorageOptions() {
      const outputPurposes = ['tx_mezzanine', 'vod_delivery', 'linear_playout', 'cdn_origin', 'transcode_output', 'stitch_output']
      return this.storageConfigs.filter(c =>
        outputPurposes.includes(c.config_name) && c.is_active
      )
    },
    // Get selected credential details for config modal
    selectedCredentialForConfig() {
      if (!this.configForm.storage_credential_id) return null
      return this.storageCredentials.find(c => c.id === this.configForm.storage_credential_id)
    },
    // IAM Policy JSON for display
    iamPolicyJson() {
      const bucketName = this.credentialForm.bucket_name || 'YOUR-BUCKET-NAME'
      return `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Action": [
        "s3:GetObject",
        "s3:PutObject",
        "s3:DeleteObject",
        "s3:ListBucket"
      ],
      "Resource": [
        "arn:aws:s3:::${bucketName}",
        "arn:aws:s3:::${bucketName}/*"
      ]
    }
  ]
}`
    }
  },
  data() {
    return {
      // Credentials
      storageCredentials: [],
      loadingCredentials: false,
      showCredentialModal: false,
      isEditingCredential: false,
      savingCredential: false,
      testingCredential: false,
      selectedCredential: null,
      showIamHelp: false,
      showLocalHelp: false,
      showSmbHelp: false,
      showNfsHelp: false,
      showSftpHelp: false,
      showPatternHelp: false,
      credentialForm: {
        storage_name: '',
        storage_provider: 's3',
        bucket_name: '',
        region: 'us-east-1',
        access_key_id: '',
        secret_access_key: '',
        endpoint_url: '',
        is_public: false,
        base_path: '',
        // Network storage fields
        network_path: '',
        smb_domain: '',
        network_username: '',
        network_password: '',
        nfs_mount_options: '',
        ftp_host: '',
        ftp_port: 22,
        ftp_use_sftp: true
      },

      // Configs
      storageConfigs: [],
      loadingConfigs: false,
      showConfigModal: false,
      isEditingConfig: false,
      savingConfig: false,
      selectedConfig: null,
      configForm: {
        config_name: '',
        storage_credential_id: null,
        bucket_name: '',
        bucket_region: '',
        base_folder: '',
        folder_template: '',
        filename_template: '',
        // Auto-suggest pattern hint
        filename_pattern_hint: '',
        sample_filenames: '',
        default_codec: '',
        default_container: '',
        default_bitrate_kbps: null,
        default_resolution: '',
        default_framerate: '',
        // Encoding preset link
        encoding_preset_id: null,
        // Processing location fields
        processing_location: null,
        client_aws_role_arn: '',
        client_aws_external_id: '',
        client_aws_region: '',
        ffmpeg_preset: '',
        mediaconvert_preset_arn: '',
        mediaconvert_queue_arn: '',
        processing_priority: 'normal',
        enable_qc_after_processing: true,
        auto_deliver_after_processing: false,
        // Gateway settings (VersityGW / SMB path mapping)
        gateway_type: 'none',
        gateway_endpoint: '',
        smb_path_mapping_enabled: false,
        smb_share_path: '',
        cache_policy: 'none',
        cache_size_gb: null,
        cache_days_ahead: 7,
        // Lifecycle management
        lifecycle_enabled: false,
        restore_days_before_air: 7,
        archive_days_after_last_air: 30,
        restore_tier: 'standard',
        storage_class_hot: 'STANDARD',
        storage_class_warm: 'STANDARD_IA',
        storage_class_archive: 'GLACIER'
      },

      // Encoding Presets
      encodingPresets: [],
      loadingPresets: false,
      showPresetModal: false,
      isEditingPreset: false,
      savingPreset: false,
      selectedPreset: null,
      presetForm: {
        preset_name: '',
        preset_code: '',
        description: '',
        video_codec: 'h264',
        video_bitrate_kbps: 17500,
        video_maxrate_kbps: null,
        video_bufsize_kbps: null,
        video_profile: 'high',
        video_level: '4.1',
        width: 1920,
        height: 1080,
        framerate: '25',
        interlaced: true,
        field_order: 'tff',
        pixel_format: 'yuv420p',
        audio_codec: 'aac',
        audio_bitrate_kbps: 192,
        audio_channels: 2,
        audio_sample_rate: 48000,
        loudness_target_lufs: -23.0,
        loudness_true_peak_dbtp: -3.0,
        loudness_range_lu: 11.0,
        apply_loudness_normalization: true,
        container_format: 'mp4',
        faststart: true,
        enable_qc_after_encode: true,
        preserve_timecode: true,
        burn_in_timecode: false,
        is_default: false
      },

      // Delete
      showDeleteModal: false,
      deleteType: '',
      selectedItem: null,

      // Workflow Configuration (legacy - for spot processing)
      workflowConfig: {
        inputStorageConfigId: null,
        outputStorageConfigId: null,
        defaultEncodingPresetId: null
      },
      loadingWorkflow: false,
      savingWorkflow: false,

      // Platform Workflow Templates (read-only, managed by super admin)
      platformWorkflows: [],
      loadingPlatformWorkflows: false,

      // Processing Workflows (named workflows with CRUD)
      processingWorkflows: [],
      loadingProcessingWorkflows: false,
      showWorkflowModal: false,
      isEditingWorkflow: false,
      savingWorkflowForm: false,
      selectedWorkflow: null,
      workflowForm: {
        workflow_name: '',
        workflow_code: '',
        description: '',
        workflow_template_id: null,
        input_storage_config_id: null,
        output_storage_config_id: null,
        encoding_preset_id: null,
        workflow_type: 'transcode',
        auto_qc_after_process: true,
        auto_deliver_after_qc: false,
        notify_on_complete: true,
        notify_on_error: true,
        priority: 'normal',
        max_concurrent_jobs: 5,
        is_default: false
      },

      // S3 Upload Watchers
      s3Watchers: [],
      s3Detections: [],
      loadingS3Watchers: false,
      loadingS3Detections: false,
      s3DetectionsDays: 7,
      showWatcherModal: false,
      isEditingWatcher: false,
      savingWatcher: false,
      watcherForm: {
        id: null,
        storage_config_id: null,
        watch_folder_path: '',
        notification_emails: '',
        file_extensions: 'mp4,mxf,mov,ts,m2ts',
        is_active: true
      }
    }
  },
  async mounted() {
    await this.loadStorageCredentials()
    await this.loadStorageConfigs()
    await this.loadEncodingPresets()
    await this.loadWorkflowConfig()
    await this.loadPlatformWorkflows()
    await this.loadProcessingWorkflows()
    await this.loadS3Watchers()
    await this.loadS3Detections()
  },
  methods: {
    // ========== IAM Policy Helper ==========
    copyIamPolicy() {
      const bucketName = this.credentialForm.bucket_name || 'YOUR-BUCKET-NAME'
      const policy = `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Action": [
        "s3:GetObject",
        "s3:PutObject",
        "s3:DeleteObject",
        "s3:ListBucket"
      ],
      "Resource": [
        "arn:aws:s3:::${bucketName}",
        "arn:aws:s3:::${bucketName}/*"
      ]
    }
  ]
}`
      navigator.clipboard.writeText(policy).then(() => {
        this.$buefy.toast.open({
          message: 'IAM policy copied to clipboard!',
          type: 'is-success',
          position: 'is-top'
        })
      }).catch(() => {
        this.$buefy.toast.open({
          message: 'Failed to copy to clipboard',
          type: 'is-danger',
          position: 'is-top'
        })
      })
    },

    // ========== Credentials Methods ==========
    async loadStorageCredentials() {
      try {
        this.loadingCredentials = true
        this.storageCredentials = await StorageCredentialsApi.getAll()
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load storage credentials',
          type: 'is-danger',
          position: 'is-top'
        })
      } finally {
        this.loadingCredentials = false
      }
    },

    getProviderTagType(provider) {
      const types = {
        s3: 'is-info',
        s3_compatible: 'is-info',
        wasabi: 'is-success',
        google: 'is-primary',
        gcp: 'is-primary',
        local: 'is-light',
        nas: 'is-warning',
        azure: 'is-link',
        smb: 'is-link',
        nfs: 'is-warning',
        sftp: 'is-primary'
      }
      return types[provider] || 'is-light'
    },

    getProviderLabel(provider) {
      const labels = {
        s3: 'AWS S3',
        s3_compatible: 'S3 Compatible',
        huawei_obs: 'Huawei OBS',
        wasabi: 'Wasabi',
        google: 'Google Cloud',
        gcp: 'Google Cloud',
        local: 'Local',
        nas: 'NAS',
        azure: 'Azure',
        smb: 'SMB/CIFS',
        nfs: 'NFS',
        sftp: 'SFTP'
      }
      return labels[provider] || provider.toUpperCase()
    },

    openCreateCredentialModal() {
      this.isEditingCredential = false
      this.resetCredentialForm()
      this.showCredentialModal = true
    },

    openEditCredentialModal(credential) {
      this.isEditingCredential = true
      this.selectedCredential = credential
      this.credentialForm = {
        storage_name: credential.storage_name,
        storage_provider: credential.storage_provider,
        bucket_name: credential.bucket_name || '',
        region: credential.region || 'us-east-1',
        access_key_id: '',
        secret_access_key: '',
        endpoint_url: credential.endpoint_url || '',
        is_public: credential.is_public,
        base_path: credential.base_path || '',
        // Network storage fields
        network_path: credential.network_path || '',
        smb_domain: credential.smb_domain || '',
        network_username: credential.network_username || '',
        network_password: '', // Never pre-fill passwords
        nfs_mount_options: credential.nfs_mount_options || '',
        ftp_host: credential.ftp_host || '',
        ftp_port: credential.ftp_port || 22,
        ftp_use_sftp: credential.ftp_use_sftp !== false // Default true
      }
      this.showCredentialModal = true
    },

    onProviderChange() {
      const provider = this.credentialForm.storage_provider
      const isCloudStorage = ['s3', 's3_compatible', 'huawei_obs', 'wasabi', 'google', 'azure'].includes(provider)
      const isLocalStorage = ['local', 'nas'].includes(provider)
      const isNetworkStorage = ['smb', 'nfs', 'sftp'].includes(provider)

      // Reset fields based on provider type
      if (isLocalStorage) {
        // Clear cloud and network fields
        this.credentialForm.bucket_name = ''
        this.credentialForm.region = ''
        this.credentialForm.access_key_id = ''
        this.credentialForm.secret_access_key = ''
        this.credentialForm.endpoint_url = ''
        this.credentialForm.is_public = false
        this.credentialForm.network_path = ''
        this.credentialForm.smb_domain = ''
        this.credentialForm.network_username = ''
        this.credentialForm.network_password = ''
        this.credentialForm.nfs_mount_options = ''
        this.credentialForm.ftp_host = ''
        this.credentialForm.ftp_port = 22
        this.credentialForm.ftp_use_sftp = true
      } else if (isNetworkStorage) {
        // Clear cloud fields
        this.credentialForm.bucket_name = ''
        this.credentialForm.region = ''
        this.credentialForm.access_key_id = ''
        this.credentialForm.secret_access_key = ''
        this.credentialForm.endpoint_url = ''
        this.credentialForm.is_public = false
        this.credentialForm.base_path = ''

        // Set SFTP-specific defaults
        if (provider === 'sftp') {
          this.credentialForm.ftp_port = 22
          this.credentialForm.ftp_use_sftp = true
        }
      } else if (isCloudStorage) {
        // Clear local and network fields
        this.credentialForm.base_path = ''
        this.credentialForm.network_path = ''
        this.credentialForm.smb_domain = ''
        this.credentialForm.network_username = ''
        this.credentialForm.network_password = ''
        this.credentialForm.nfs_mount_options = ''
        this.credentialForm.ftp_host = ''
        this.credentialForm.ftp_port = 22
        this.credentialForm.ftp_use_sftp = true
      }

      // Set default region and auto-populate endpoint for cloud providers
      if (provider === 'wasabi') {
        // Default to us-east-1 for Wasabi
        if (!this.credentialForm.region) {
          this.credentialForm.region = 'us-east-1'
        }
        // Auto-populate endpoint URL based on region
        this.credentialForm.endpoint_url = this.getWasabiEndpoint(this.credentialForm.region)
      } else if (provider === 's3') {
        // Clear endpoint for AWS S3 (uses default AWS endpoints)
        this.credentialForm.endpoint_url = ''
        if (!this.credentialForm.region) {
          this.credentialForm.region = 'us-east-1'
        }
      } else if (provider === 's3_compatible') {
        // Keep region and endpoint as user must specify
        if (!this.credentialForm.region) {
          this.credentialForm.region = 'us-east-1'
        }
      } else if (provider === 'huawei_obs') {
        // Huawei OBS - default to af-south-1 (Johannesburg) for South Africa
        if (!this.credentialForm.region) {
          this.credentialForm.region = 'af-south-1'
        }
        // Auto-populate endpoint based on region
        this.credentialForm.endpoint_url = this.getHuaweiObsEndpoint(this.credentialForm.region)
      }
    },

    onRegionChange() {
      // Auto-update endpoint URL when region changes for Wasabi
      if (this.credentialForm.storage_provider === 'wasabi') {
        this.credentialForm.endpoint_url = this.getWasabiEndpoint(this.credentialForm.region)
      } else if (this.credentialForm.storage_provider === 'huawei_obs') {
        this.credentialForm.endpoint_url = this.getHuaweiObsEndpoint(this.credentialForm.region)
      }
    },

    getWasabiEndpoint(region) {
      // Wasabi endpoint mapping - they use s3.{region}.wasabisys.com format
      const wasabiEndpoints = {
        'us-east-1': 'https://s3.us-east-1.wasabisys.com',
        'us-east-2': 'https://s3.us-east-2.wasabisys.com',
        'us-west-1': 'https://s3.us-west-1.wasabisys.com',
        'us-central-1': 'https://s3.us-central-1.wasabisys.com',
        'ca-central-1': 'https://s3.ca-central-1.wasabisys.com',
        'eu-central-1': 'https://s3.eu-central-1.wasabisys.com',
        'eu-central-2': 'https://s3.eu-central-2.wasabisys.com',
        'eu-west-1': 'https://s3.eu-west-1.wasabisys.com',
        'eu-west-2': 'https://s3.eu-west-2.wasabisys.com',
        'ap-northeast-1': 'https://s3.ap-northeast-1.wasabisys.com',
        'ap-northeast-2': 'https://s3.ap-northeast-2.wasabisys.com',
        'ap-southeast-1': 'https://s3.ap-southeast-1.wasabisys.com',
        'ap-southeast-2': 'https://s3.ap-southeast-2.wasabisys.com'
      }
      return wasabiEndpoints[region] || `https://s3.${region}.wasabisys.com`
    },

    getHuaweiObsEndpoint(region) {
      // Huawei OBS endpoint format: obs.{region}.myhuaweicloud.com
      // Common regions: af-south-1 (Johannesburg), ap-southeast-1 (Hong Kong),
      // ap-southeast-2 (Bangkok), ap-southeast-3 (Singapore), eu-west-0 (Paris), etc.
      return `https://obs.${region}.myhuaweicloud.com`
    },

    resetCredentialForm() {
      this.credentialForm = {
        storage_name: '',
        storage_provider: 's3',
        bucket_name: '',
        region: 'us-east-1',
        access_key_id: '',
        secret_access_key: '',
        endpoint_url: '',
        is_public: false,
        base_path: '',
        // Network storage fields
        network_path: '',
        smb_domain: '',
        network_username: '',
        network_password: '',
        nfs_mount_options: '',
        ftp_host: '',
        ftp_port: 22,
        ftp_use_sftp: true
      }
      this.selectedCredential = null
    },

    closeCredentialModal() {
      this.showCredentialModal = false
      this.resetCredentialForm()
    },

    async testCredentialBeforeSave() {
      try {
        this.testingCredential = true
        const testData = { ...this.credentialForm }
        const result = await StorageCredentialsApi.testConnection(testData)

        if (result.success) {
          this.$buefy.toast.open({
            message: `Connection successful! ${result.message}`,
            type: 'is-success',
            position: 'is-top',
            duration: 5000
          })
        } else {
          throw new Error(result.error || 'Connection test failed')
        }
      } catch (error) {
        this.$buefy.toast.open({
          message: error.response?.data?.error || error.message || 'Connection test failed',
          type: 'is-danger',
          position: 'is-top',
          duration: 5000
        })
      } finally {
        this.testingCredential = false
      }
    },

    async testCredentialConnection(credential) {
      try {
        this.loadingCredentials = true
        const result = await StorageCredentialsApi.testConnection({ id: credential.id })

        if (result.success) {
          this.$buefy.dialog.alert({
            title: 'Connection Test Successful',
            message: `<p><strong>${result.message}</strong></p>`,
            confirmText: 'OK',
            type: 'is-success'
          })
        } else {
          throw new Error(result.error || 'Connection test failed')
        }
      } catch (error) {
        this.$buefy.toast.open({
          message: error.response?.data?.error || error.message || 'Connection test failed',
          type: 'is-danger',
          position: 'is-top',
          duration: 5000
        })
      } finally {
        this.loadingCredentials = false
      }
    },

    async saveCredential() {
      try {
        this.savingCredential = true

        if (!this.credentialForm.storage_name || !this.credentialForm.storage_provider) {
          throw new Error('Storage name and provider are required')
        }

        if (this.isEditingCredential) {
          await StorageCredentialsApi.update(this.selectedCredential.id, this.credentialForm)
          this.$buefy.toast.open({
            message: 'Storage credential updated successfully',
            type: 'is-success',
            position: 'is-top'
          })
        } else {
          await StorageCredentialsApi.create(this.credentialForm)
          this.$buefy.toast.open({
            message: 'Storage credential created successfully',
            type: 'is-success',
            position: 'is-top'
          })
        }

        await this.loadStorageCredentials()
        this.closeCredentialModal()
      } catch (error) {
        this.$buefy.toast.open({
          message: error.response?.data?.error || error.message || 'Failed to save storage credential',
          type: 'is-danger',
          position: 'is-top',
          duration: 5000
        })
      } finally {
        this.savingCredential = false
      }
    },

    confirmDeleteCredential(credential) {
      this.selectedItem = credential
      this.deleteType = 'storage credential'
      this.showDeleteModal = true
    },

    async toggleCredentialActive(credential) {
      try {
        const result = await StorageCredentialsApi.toggleActive(credential.id)
        this.$buefy.toast.open({
          message: result.message,
          type: 'is-success',
          position: 'is-top'
        })
        // Reload credentials - inactive ones will be filtered out
        await this.loadStorageCredentials()
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to toggle credential status',
          type: 'is-danger',
          position: 'is-top'
        })
      }
    },

    // ========== Config Methods ==========
    async loadStorageConfigs() {
      try {
        this.loadingConfigs = true
        this.storageConfigs = await StorageConfigApi.getAll()
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load storage configurations',
          type: 'is-danger',
          position: 'is-top'
        })
      } finally {
        this.loadingConfigs = false
      }
    },

    getConfigTagType(configName) {
      const types = {
        // Source / Input - Blue tones
        source_input: 'is-info',
        library: 'is-info',
        archive: 'is-info',
        watch_folder: 'is-info',
        // Processing / Work - Warning tones
        work_temp: 'is-warning',
        transcode_output: 'is-warning',
        stitch_output: 'is-warning',
        qc_output: 'is-link',
        // Delivery / Output - Success tones
        tx_mezzanine: 'is-success',
        vod_delivery: 'is-success',
        linear_playout: 'is-success',
        cdn_origin: 'is-success',
        // Preview / Review - Light tones
        proxy: 'is-light',
        thumbnail: 'is-light',
        review_approval: 'is-primary'
      }
      return types[configName] || 'is-light'
    },

    formatConfigName(configName) {
      const names = {
        // Source / Input
        source_input: 'Source Input (Ingest)',
        library: 'Library',
        archive: 'Archive',
        watch_folder: 'Watch Folder',
        // Processing / Work
        work_temp: 'Work/Temp',
        transcode_output: 'Transcode Output',
        stitch_output: 'Stitch Output',
        qc_output: 'QC Output',
        // Delivery / Output
        tx_mezzanine: 'TX Mezzanine',
        vod_delivery: 'VOD Delivery',
        linear_playout: 'Linear Playout',
        cdn_origin: 'CDN Origin',
        // Preview / Review
        proxy: 'Proxy',
        thumbnail: 'Thumbnail',
        review_approval: 'Review & Approval'
      }
      return names[configName] || configName
    },

    getProcessingTagType(location) {
      const types = {
        bms_mediaconvert: 'is-info',
        bms_ffmpeg: 'is-success',
        client_aws: 'is-warning',
        local: 'is-light'
      }
      return types[location] || 'is-light'
    },

    getProcessingLabel(location) {
      const labels = {
        bms_mediaconvert: 'MediaConvert',
        bms_ffmpeg: 'FFmpeg',
        client_aws: 'Client AWS',
        local: 'Local'
      }
      return labels[location] || location
    },

    getCredentialName(credentialId) {
      const cred = this.storageCredentials.find(c => c.id === credentialId)
      return cred ? cred.storage_name : '-'
    },

    openCreateConfigModal() {
      this.isEditingConfig = false
      this.resetConfigForm()
      this.showConfigModal = true
    },

    openEditConfigModal(config) {
      this.isEditingConfig = true
      this.selectedConfig = config
      this.configForm = {
        config_name: config.config_name,
        storage_credential_id: config.storage_credential_id,
        bucket_name: config.bucket_name || '',
        bucket_region: config.bucket_region || '',
        base_folder: config.base_folder || '',
        folder_template: config.folder_template || '',
        filename_template: config.filename_template || '',
        // Auto-suggest pattern hint
        filename_pattern_hint: config.filename_pattern_hint || '',
        sample_filenames: config.sample_filenames || '',
        default_codec: config.default_codec || '',
        default_container: config.default_container || '',
        default_bitrate_kbps: config.default_bitrate_kbps || null,
        default_resolution: config.default_resolution || '',
        default_framerate: config.default_framerate || '',
        // Encoding preset link
        encoding_preset_id: config.encoding_preset_id || null,
        // Processing location fields
        processing_location: config.processing_location || null,
        client_aws_role_arn: config.client_aws_role_arn || '',
        client_aws_external_id: config.client_aws_external_id || '',
        client_aws_region: config.client_aws_region || '',
        ffmpeg_preset: config.ffmpeg_preset || '',
        mediaconvert_preset_arn: config.mediaconvert_preset_arn || '',
        mediaconvert_queue_arn: config.mediaconvert_queue_arn || '',
        processing_priority: config.processing_priority || 'normal',
        enable_qc_after_processing: config.enable_qc_after_processing == null ? true : (config.enable_qc_after_processing === 1 || config.enable_qc_after_processing === true),
        auto_deliver_after_processing: config.auto_deliver_after_processing === 1 || config.auto_deliver_after_processing === true,
        // Gateway settings (VersityGW / SMB path mapping)
        gateway_type: config.gateway_type || 'none',
        gateway_endpoint: config.gateway_endpoint || '',
        smb_path_mapping_enabled: config.smb_path_mapping_enabled === 1 || config.smb_path_mapping_enabled === true,
        smb_share_path: config.smb_share_path || '',
        cache_policy: config.cache_policy || 'none',
        cache_size_gb: config.cache_size_gb || null,
        cache_days_ahead: config.cache_days_ahead || 7,
        // Lifecycle management
        lifecycle_enabled: config.lifecycle_enabled === 1 || config.lifecycle_enabled === true,
        restore_days_before_air: config.restore_days_before_air || 7,
        archive_days_after_last_air: config.archive_days_after_last_air || 30,
        restore_tier: config.restore_tier || 'standard',
        storage_class_hot: config.storage_class_hot || 'STANDARD',
        storage_class_warm: config.storage_class_warm || 'STANDARD_IA',
        storage_class_archive: config.storage_class_archive || 'GLACIER'
      }
      this.showConfigModal = true
    },

    onCredentialSelected(credentialId) {
      // When a credential is selected, pre-fill bucket from credential's default
      const cred = this.storageCredentials.find(c => c.id === credentialId)
      if (cred && !this.configForm.bucket_name) {
        this.configForm.bucket_name = cred.bucket_name || ''
        this.configForm.bucket_region = cred.region || ''
      }
    },

    resetConfigForm() {
      this.configForm = {
        config_name: '',
        storage_credential_id: null,
        bucket_name: '',
        bucket_region: '',
        base_folder: '',
        folder_template: '',
        filename_template: '',
        // Auto-suggest pattern hint
        filename_pattern_hint: '',
        sample_filenames: '',
        default_codec: '',
        default_container: '',
        default_bitrate_kbps: null,
        default_resolution: '',
        default_framerate: '',
        // Encoding preset link
        encoding_preset_id: null,
        // Processing location fields
        processing_location: null,
        client_aws_role_arn: '',
        client_aws_external_id: '',
        client_aws_region: '',
        ffmpeg_preset: '',
        mediaconvert_preset_arn: '',
        mediaconvert_queue_arn: '',
        processing_priority: 'normal',
        enable_qc_after_processing: true,
        auto_deliver_after_processing: false,
        // Gateway settings (VersityGW / SMB path mapping)
        gateway_type: 'none',
        gateway_endpoint: '',
        smb_path_mapping_enabled: false,
        smb_share_path: '',
        cache_policy: 'none',
        cache_size_gb: null,
        cache_days_ahead: 7,
        // Lifecycle management
        lifecycle_enabled: false,
        restore_days_before_air: 7,
        archive_days_after_last_air: 30,
        restore_tier: 'standard',
        storage_class_hot: 'STANDARD',
        storage_class_warm: 'STANDARD_IA',
        storage_class_archive: 'GLACIER'
      }
      this.selectedConfig = null
    },

    closeConfigModal() {
      this.showConfigModal = false
      this.resetConfigForm()
    },

    applyPreset(presetName) {
      if (presetName === 'tx_archive') {
        this.configForm.default_codec = 'h264'
        this.configForm.default_container = 'mp4'
        this.configForm.default_bitrate_kbps = 20000
        this.configForm.default_resolution = '1920x1080'
        this.configForm.default_framerate = '25'
      } else if (presetName === 'vod_xdcam') {
        this.configForm.default_codec = 'xdcam_50i'
        this.configForm.default_container = 'mxf'
        this.configForm.default_bitrate_kbps = 50000
        this.configForm.default_resolution = '1920x1080'
        this.configForm.default_framerate = '50i'
      } else if (presetName === 'proxy') {
        this.configForm.default_codec = 'h264'
        this.configForm.default_container = 'mp4'
        this.configForm.default_bitrate_kbps = 2000
        this.configForm.default_resolution = '1280x720'
        this.configForm.default_framerate = '25'
      }
    },

    async testConfigConnection(config) {
      try {
        this.loadingConfigs = true
        const result = await StorageConfigApi.testConnection(config.id)

        if (result.success) {
          this.$buefy.dialog.alert({
            title: 'Path Test Successful',
            message: `<p><strong>${result.message}</strong></p>`,
            confirmText: 'OK',
            type: 'is-success'
          })
        } else {
          throw new Error(result.error || 'Path test failed')
        }
      } catch (error) {
        this.$buefy.toast.open({
          message: error.response?.data?.error || error.message || 'Path test failed',
          type: 'is-danger',
          position: 'is-top',
          duration: 5000
        })
      } finally {
        this.loadingConfigs = false
      }
    },

    async saveConfig() {
      try {
        this.savingConfig = true

        if (!this.configForm.config_name || !this.configForm.storage_credential_id) {
          throw new Error('Configuration purpose and storage credential are required')
        }

        if (this.isEditingConfig) {
          await StorageConfigApi.update(this.selectedConfig.id, this.configForm)
          this.$buefy.toast.open({
            message: 'Path configuration updated successfully',
            type: 'is-success',
            position: 'is-top'
          })
        } else {
          await StorageConfigApi.create(this.configForm)
          this.$buefy.toast.open({
            message: 'Path configuration created successfully',
            type: 'is-success',
            position: 'is-top'
          })
        }

        await this.loadStorageConfigs()
        this.closeConfigModal()
      } catch (error) {
        this.$buefy.toast.open({
          message: error.response?.data?.error || error.message || 'Failed to save path configuration',
          type: 'is-danger',
          position: 'is-top',
          duration: 5000
        })
      } finally {
        this.savingConfig = false
      }
    },

    confirmDeleteConfig(config) {
      this.selectedItem = config
      this.deleteType = 'path configuration'
      this.showDeleteModal = true
    },

    // ========== Delete Methods ==========
    async executeDelete() {
      try {
        if (this.deleteType === 'storage credential') {
          await StorageCredentialsApi.delete(this.selectedItem.id)
          await this.loadStorageCredentials()
        } else if (this.deleteType === 'encoding preset') {
          await EncodingPresetsApi.delete(this.selectedItem.id)
          await this.loadEncodingPresets()
        } else if (this.deleteType === 'workflow') {
          await ProcessingWorkflowsApi.delete(this.selectedItem.id)
          await this.loadProcessingWorkflows()
        } else {
          await StorageConfigApi.delete(this.selectedItem.id)
          await this.loadStorageConfigs()
        }

        this.$buefy.toast.open({
          message: `${this.deleteType} deleted successfully`,
          type: 'is-success',
          position: 'is-top'
        })
        this.showDeleteModal = false
      } catch (error) {
        this.$buefy.toast.open({
          message: error.response?.data?.error || `Failed to delete ${this.deleteType}`,
          type: 'is-danger',
          position: 'is-top',
          duration: 5000
        })
      }
    },

    // ========== Encoding Presets Methods ==========
    async loadEncodingPresets() {
      try {
        this.loadingPresets = true
        this.encodingPresets = await EncodingPresetsApi.getAll()
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load encoding presets',
          type: 'is-danger',
          position: 'is-top'
        })
      } finally {
        this.loadingPresets = false
      }
    },

    getCodecLabel(codec) {
      const labels = {
        h264: 'H.264',
        h265: 'H.265',
        prores_422: 'ProRes 422',
        prores_422hq: 'ProRes 422 HQ',
        prores_4444: 'ProRes 4444',
        xdcam_hd: 'XDCAM HD',
        xdcam_50: 'XDCAM 50',
        dnxhd_185: 'DNxHD 185',
        dnxhd_120: 'DNxHD 120',
        dnxhr_hq: 'DNxHR HQ',
        mpeg2: 'MPEG-2'
      }
      return labels[codec] || codec
    },

    getGatewayLabel(gatewayType) {
      const labels = {
        none: 'No Gateway',
        versitygw: 'VersityGW',
        rclone: 'rclone Mount',
        minio_gateway: 'MinIO Gateway',
        other: 'Custom Gateway'
      }
      return labels[gatewayType] || gatewayType
    },

    formatBitrate(kbps) {
      if (!kbps) return '-'
      if (kbps >= 1000) {
        return `${(kbps / 1000).toFixed(1)} Mbps`
      }
      return `${kbps} kbps`
    },

    openCreatePresetModal() {
      this.isEditingPreset = false
      this.resetPresetForm()
      this.showPresetModal = true
    },

    openEditPresetModal(preset) {
      this.isEditingPreset = true
      this.selectedPreset = preset
      // Convert MySQL tinyint (0/1) to JavaScript booleans for Buefy checkboxes
      this.presetForm = {
        ...preset,
        interlaced: preset.interlaced === 1 || preset.interlaced === true,
        apply_loudness_normalization: preset.apply_loudness_normalization === 1 || preset.apply_loudness_normalization === true,
        faststart: preset.faststart === 1 || preset.faststart === true,
        enable_qc_after_encode: preset.enable_qc_after_encode === 1 || preset.enable_qc_after_encode === true,
        preserve_timecode: preset.preserve_timecode === 1 || preset.preserve_timecode === true,
        burn_in_timecode: preset.burn_in_timecode === 1 || preset.burn_in_timecode === true
      }
      this.showPresetModal = true
    },

    resetPresetForm() {
      this.presetForm = {
        preset_name: '',
        preset_code: '',
        description: '',
        video_codec: 'h264',
        video_bitrate_kbps: 17500,
        video_maxrate_kbps: null,
        video_bufsize_kbps: null,
        video_profile: 'high',
        video_level: '4.1',
        width: 1920,
        height: 1080,
        framerate: '25',
        interlaced: true,
        field_order: 'tff',
        pixel_format: 'yuv420p',
        audio_codec: 'aac',
        audio_bitrate_kbps: 192,
        audio_channels: 2,
        audio_sample_rate: 48000,
        loudness_target_lufs: -23.0,
        loudness_true_peak_dbtp: -3.0,
        loudness_range_lu: 11.0,
        apply_loudness_normalization: true,
        container_format: 'mp4',
        faststart: true,
        enable_qc_after_encode: true,
        preserve_timecode: true,
        burn_in_timecode: false,
        is_default: false
      }
      this.selectedPreset = null
    },

    closePresetModal() {
      this.showPresetModal = false
      this.resetPresetForm()
    },

    applyQuickPreset(presetName) {
      if (presetName === 'tx_master') {
        this.presetForm.video_codec = 'h264'
        this.presetForm.video_bitrate_kbps = 17500
        this.presetForm.video_maxrate_kbps = 20000
        this.presetForm.video_bufsize_kbps = 40000
        this.presetForm.video_profile = 'high'
        this.presetForm.video_level = '4.1'
        this.presetForm.width = 1920
        this.presetForm.height = 1080
        this.presetForm.framerate = '25'
        this.presetForm.interlaced = true
        this.presetForm.container_format = 'mp4'
        this.presetForm.audio_bitrate_kbps = 192
      } else if (presetName === 'vod_hd') {
        this.presetForm.video_codec = 'h264'
        this.presetForm.video_bitrate_kbps = 8000
        this.presetForm.video_maxrate_kbps = 10000
        this.presetForm.video_bufsize_kbps = 16000
        this.presetForm.video_profile = 'high'
        this.presetForm.video_level = '4.0'
        this.presetForm.width = 1920
        this.presetForm.height = 1080
        this.presetForm.framerate = '25'
        this.presetForm.interlaced = false
        this.presetForm.container_format = 'mp4'
        this.presetForm.audio_bitrate_kbps = 128
      } else if (presetName === 'proxy') {
        this.presetForm.video_codec = 'h264'
        this.presetForm.video_bitrate_kbps = 2000
        this.presetForm.video_maxrate_kbps = 2500
        this.presetForm.video_bufsize_kbps = 4000
        this.presetForm.video_profile = 'main'
        this.presetForm.video_level = '3.1'
        this.presetForm.width = 1280
        this.presetForm.height = 720
        this.presetForm.framerate = '25'
        this.presetForm.interlaced = false
        this.presetForm.container_format = 'mp4'
        this.presetForm.audio_bitrate_kbps = 96
        this.presetForm.enable_qc_after_encode = false
      } else if (presetName === 'archive') {
        this.presetForm.video_codec = 'h264'
        this.presetForm.video_bitrate_kbps = 50000
        this.presetForm.video_maxrate_kbps = 60000
        this.presetForm.video_bufsize_kbps = 100000
        this.presetForm.video_profile = 'high'
        this.presetForm.video_level = '5.0'
        this.presetForm.width = 1920
        this.presetForm.height = 1080
        this.presetForm.framerate = '25'
        this.presetForm.interlaced = true
        this.presetForm.container_format = 'mxf'
        this.presetForm.audio_bitrate_kbps = 256
      }
    },

    applyLoudnessStandard(standard) {
      this.presetForm.apply_loudness_normalization = true
      if (standard === 'ebu') {
        this.presetForm.loudness_target_lufs = -23.0
        this.presetForm.loudness_true_peak_dbtp = -3.0
        this.presetForm.loudness_range_lu = 11.0
      } else if (standard === 'atsc') {
        this.presetForm.loudness_target_lufs = -24.0
        this.presetForm.loudness_true_peak_dbtp = -2.0
        this.presetForm.loudness_range_lu = 11.0
      } else if (standard === 'op59') {
        this.presetForm.loudness_target_lufs = -27.0
        this.presetForm.loudness_true_peak_dbtp = -2.0
        this.presetForm.loudness_range_lu = 11.0
      }
    },

    async savePreset() {
      try {
        this.savingPreset = true

        if (!this.presetForm.preset_name) {
          throw new Error('Preset name is required')
        }

        if (this.isEditingPreset) {
          await EncodingPresetsApi.update(this.selectedPreset.id, this.presetForm)
          this.$buefy.toast.open({
            message: 'Encoding preset updated successfully',
            type: 'is-success',
            position: 'is-top'
          })
        } else {
          await EncodingPresetsApi.create(this.presetForm)
          this.$buefy.toast.open({
            message: 'Encoding preset created successfully',
            type: 'is-success',
            position: 'is-top'
          })
        }

        await this.loadEncodingPresets()
        this.closePresetModal()
      } catch (error) {
        this.$buefy.toast.open({
          message: error.response?.data?.error || error.message || 'Failed to save encoding preset',
          type: 'is-danger',
          position: 'is-top',
          duration: 5000
        })
      } finally {
        this.savingPreset = false
      }
    },

    async setPresetAsDefault(preset) {
      try {
        await EncodingPresetsApi.setDefault(preset.id)
        await this.loadEncodingPresets()
        this.$buefy.toast.open({
          message: `${preset.preset_name} set as default`,
          type: 'is-success',
          position: 'is-top'
        })
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to set default preset',
          type: 'is-danger',
          position: 'is-top'
        })
      }
    },

    async duplicatePreset(preset) {
      try {
        const newName = `${preset.preset_name} (Copy)`
        await EncodingPresetsApi.duplicate(preset.id, newName)
        await this.loadEncodingPresets()
        this.$buefy.toast.open({
          message: 'Preset duplicated successfully',
          type: 'is-success',
          position: 'is-top'
        })
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to duplicate preset',
          type: 'is-danger',
          position: 'is-top'
        })
      }
    },

    confirmDeletePreset(preset) {
      this.selectedItem = preset
      this.deleteType = 'encoding preset'
      this.showDeleteModal = true
    },

    // ========== Workflow Configuration Methods ==========
    async loadWorkflowConfig() {
      try {
        this.loadingWorkflow = true
        const response = await SpotProcessingApi.getConfig()
        if (response && response.config) {
          const config = response.config
          this.workflowConfig = {
            inputStorageConfigId: config.input_storage_config_id || null,
            outputStorageConfigId: config.output_storage_config_id || null,
            defaultEncodingPresetId: config.default_encoding_preset_id || null
          }
        }
      } catch (error) {
        // Config may not exist yet, that's fine
        console.log('No workflow config found, using defaults')
      } finally {
        this.loadingWorkflow = false
      }
    },

    async saveWorkflowConfig() {
      try {
        this.savingWorkflow = true
        const payload = {
          input_storage_config_id: this.workflowConfig.inputStorageConfigId,
          output_storage_config_id: this.workflowConfig.outputStorageConfigId,
          default_encoding_preset_id: this.workflowConfig.defaultEncodingPresetId
        }
        console.log('Saving workflow config:', payload)
        await SpotProcessingApi.updateWorkflowConfig(payload)
        this.$buefy.toast.open({
          message: 'Workflow configuration saved',
          type: 'is-success',
          position: 'is-top'
        })
      } catch (error) {
        console.error('Workflow save error:', error.response?.status, error.response?.data || error.message)
        this.$buefy.toast.open({
          message: error.response?.data?.error || `Failed to save workflow configuration (${error.response?.status || 'network error'})`,
          type: 'is-danger',
          position: 'is-top',
          duration: 5000
        })
      } finally {
        this.savingWorkflow = false
      }
    },

    // ========== Platform Workflow Templates Methods (Read-Only) ==========
    async loadPlatformWorkflows() {
      try {
        this.loadingPlatformWorkflows = true
        const response = await WorkflowTemplatesApi.getAvailable()
        this.platformWorkflows = response.workflows || []
      } catch (error) {
        console.log('No platform workflows available or error loading:', error)
        this.platformWorkflows = []
      } finally {
        this.loadingPlatformWorkflows = false
      }
    },

    getWorkflowCategoryTagType(category) {
      const types = {
        ingest: 'is-info',
        transcode: 'is-primary',
        qc: 'is-warning',
        delivery: 'is-success',
        archive: 'is-dark',
        utility: 'is-light'
      }
      return types[category] || 'is-light'
    },

    getWorkflowCategoryLabel(category) {
      const labels = {
        ingest: 'Ingest',
        transcode: 'Transcode',
        qc: 'QC',
        delivery: 'Delivery',
        archive: 'Archive',
        utility: 'Utility'
      }
      return labels[category] || category
    },

    formatInputFormats(formats) {
      if (!formats || !Array.isArray(formats)) return '-'
      if (formats.includes('*')) return 'Any'
      return formats.slice(0, 4).map(f => f.toUpperCase()).join(', ') + (formats.length > 4 ? '...' : '')
    },

    formatOutputFormats(formats) {
      if (!formats || !Array.isArray(formats) || formats.length === 0) return 'N/A'
      if (formats.includes('*')) return 'Any'
      return formats.map(f => f.toUpperCase()).join(', ')
    },

    // ========== Processing Workflows Methods (Named Workflows CRUD) ==========
    async loadProcessingWorkflows() {
      try {
        this.loadingProcessingWorkflows = true
        this.processingWorkflows = await ProcessingWorkflowsApi.getAll()
      } catch (error) {
        console.log('No workflows found or error loading:', error)
        this.processingWorkflows = []
      } finally {
        this.loadingProcessingWorkflows = false
      }
    },

    openCreateWorkflowModal() {
      this.isEditingWorkflow = false
      this.resetWorkflowForm()
      this.showWorkflowModal = true
    },

    openEditWorkflowModal(workflow) {
      this.isEditingWorkflow = true
      this.selectedWorkflow = workflow
      this.workflowForm = {
        workflow_name: workflow.workflow_name,
        workflow_code: workflow.workflow_code || '',
        description: workflow.description || '',
        workflow_template_id: workflow.workflow_template_id || null,
        input_storage_config_id: workflow.input_storage_config_id,
        output_storage_config_id: workflow.output_storage_config_id,
        encoding_preset_id: workflow.encoding_preset_id,
        workflow_type: workflow.workflow_type || 'transcode',
        auto_qc_after_process: workflow.auto_qc_after_process,
        auto_deliver_after_qc: workflow.auto_deliver_after_qc,
        notify_on_complete: workflow.notify_on_complete,
        notify_on_error: workflow.notify_on_error,
        priority: workflow.priority || 'normal',
        max_concurrent_jobs: workflow.max_concurrent_jobs || 5,
        is_default: workflow.is_default
      }
      this.showWorkflowModal = true
    },

    resetWorkflowForm() {
      this.workflowForm = {
        workflow_name: '',
        workflow_code: '',
        description: '',
        workflow_template_id: null,
        input_storage_config_id: null,
        output_storage_config_id: null,
        encoding_preset_id: null,
        workflow_type: 'transcode',
        auto_qc_after_process: true,
        auto_deliver_after_qc: false,
        notify_on_complete: true,
        notify_on_error: true,
        priority: 'normal',
        max_concurrent_jobs: 5,
        is_default: false
      }
      this.selectedWorkflow = null
    },

    closeWorkflowModal() {
      this.showWorkflowModal = false
      this.resetWorkflowForm()
    },

    async saveProcessingWorkflow() {
      if (!this.workflowForm.workflow_name) {
        this.$buefy.toast.open({
          message: 'Workflow name is required',
          type: 'is-danger',
          position: 'is-top'
        })
        return
      }

      try {
        this.savingWorkflowForm = true

        if (this.isEditingWorkflow && this.selectedWorkflow) {
          await ProcessingWorkflowsApi.update(this.selectedWorkflow.id, this.workflowForm)
          this.$buefy.toast.open({
            message: 'Workflow updated successfully',
            type: 'is-success',
            position: 'is-top'
          })
        } else {
          await ProcessingWorkflowsApi.create(this.workflowForm)
          this.$buefy.toast.open({
            message: 'Workflow created successfully',
            type: 'is-success',
            position: 'is-top'
          })
        }

        this.closeWorkflowModal()
        await this.loadProcessingWorkflows()
      } catch (error) {
        this.$buefy.toast.open({
          message: error.response?.data?.error || 'Failed to save workflow',
          type: 'is-danger',
          position: 'is-top'
        })
      } finally {
        this.savingWorkflowForm = false
      }
    },

    async setWorkflowAsDefault(workflow) {
      try {
        await ProcessingWorkflowsApi.setDefault(workflow.id)
        this.$buefy.toast.open({
          message: `"${workflow.workflow_name}" is now the default workflow`,
          type: 'is-success',
          position: 'is-top'
        })
        await this.loadProcessingWorkflows()
      } catch (error) {
        this.$buefy.toast.open({
          message: error.response?.data?.error || 'Failed to set default workflow',
          type: 'is-danger',
          position: 'is-top'
        })
      }
    },

    confirmDeleteWorkflow(workflow) {
      this.selectedItem = workflow
      this.deleteType = 'workflow'
      this.showDeleteModal = true
    },

    async deleteWorkflow() {
      if (!this.selectedItem) return
      try {
        await ProcessingWorkflowsApi.delete(this.selectedItem.id)
        this.$buefy.toast.open({
          message: 'Workflow deleted successfully',
          type: 'is-success',
          position: 'is-top'
        })
        await this.loadProcessingWorkflows()
      } catch (error) {
        this.$buefy.toast.open({
          message: error.response?.data?.error || 'Failed to delete workflow',
          type: 'is-danger',
          position: 'is-top'
        })
      }
    },

    getWorkflowTypeLabel(type) {
      const labels = {
        transcode: 'Transcode',
        stitch: 'Stitch',
        qc_only: 'QC Only',
        copy: 'Copy',
        archive: 'Archive'
      }
      return labels[type] || type
    },

    getWorkflowTypeTagType(type) {
      const types = {
        transcode: 'is-info',
        stitch: 'is-success',
        qc_only: 'is-warning',
        copy: 'is-light',
        archive: 'is-dark'
      }
      return types[type] || 'is-light'
    },

    getPriorityTagType(priority) {
      const types = {
        low: 'is-light',
        normal: 'is-info',
        high: 'is-warning',
        urgent: 'is-danger'
      }
      return types[priority] || 'is-light'
    },

    getStorageConfigName(id) {
      if (!id) return '-'
      const config = this.storageConfigs.find(c => c.id === id)
      return config ? this.formatConfigName(config.config_name) : '-'
    },

    getEncodingPresetName(id) {
      if (!id) return '-'
      const preset = this.encodingPresets.find(p => p.id === id)
      return preset ? preset.preset_name : '-'
    },

    // ========== S3 Upload Watchers ==========
    async loadS3Watchers() {
      this.loadingS3Watchers = true
      try {
        const response = await S3UploadsApi.getWatchers()
        // API returns { success: true, watchers: [...] }
        this.s3Watchers = response.watchers || []
      } catch (error) {
        console.error('Failed to load S3 watchers:', error)
        this.s3Watchers = []
      } finally {
        this.loadingS3Watchers = false
      }
    },

    async loadS3Detections() {
      this.loadingS3Detections = true
      try {
        const response = await S3UploadsApi.getDetections({ days: this.s3DetectionsDays, limit: 100 })
        // API returns { success: true, detections: [...], stats: {...} }
        this.s3Detections = response.detections || []
      } catch (error) {
        console.error('Failed to load S3 detections:', error)
        this.s3Detections = []
      } finally {
        this.loadingS3Detections = false
      }
    },

    async refreshS3Detections() {
      await this.loadS3Detections()
    },

    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    formatDetectionDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    getWatcherBucketName(watcherId) {
      const watcher = this.s3Watchers.find(w => w.id === watcherId)
      return watcher ? watcher.bucket_name : '-'
    },

    getStorageConfigName(configId) {
      const config = this.storageConfigs.find(c => c.id === configId)
      return config ? this.formatConfigName(config.config_name) : '-'
    },

    openCreateWatcherModal() {
      this.isEditingWatcher = false
      this.watcherForm = {
        id: null,
        storage_config_id: null,
        watch_folder_path: '',
        notification_emails: '',
        file_extensions: 'mp4,mxf,mov,ts,m2ts',
        is_active: true
      }
      this.showWatcherModal = true
    },

    openEditWatcherModal(watcher) {
      this.isEditingWatcher = true
      this.watcherForm = {
        id: watcher.id,
        storage_config_id: watcher.storage_config_id,
        watch_folder_path: watcher.watch_folder_path || '',
        notification_emails: watcher.notification_emails || '',
        file_extensions: watcher.file_extensions || 'mp4,mxf,mov,ts,m2ts',
        is_active: watcher.is_active !== false
      }
      this.showWatcherModal = true
    },

    closeWatcherModal() {
      this.showWatcherModal = false
      this.watcherForm = {
        id: null,
        storage_config_id: null,
        watch_folder_path: '',
        notification_emails: '',
        file_extensions: 'mp4,mxf,mov,ts,m2ts',
        is_active: true
      }
    },

    async saveWatcher() {
      if (!this.watcherForm.storage_config_id) {
        this.$buefy.toast.open({
          message: 'Please select a storage configuration',
          type: 'is-warning'
        })
        return
      }

      this.savingWatcher = true
      try {
        if (this.isEditingWatcher) {
          await S3UploadsApi.updateWatcher(this.watcherForm.id, {
            storage_config_id: this.watcherForm.storage_config_id,
            watch_folder_path: this.watcherForm.watch_folder_path,
            notification_emails: this.watcherForm.notification_emails,
            file_extensions: this.watcherForm.file_extensions,
            is_active: this.watcherForm.is_active
          })
          this.$buefy.toast.open({
            message: 'Watcher updated successfully',
            type: 'is-success'
          })
        } else {
          // Create new watcher
          const createResponse = await S3UploadsApi.createWatcher({
            storage_config_id: this.watcherForm.storage_config_id,
            watch_folder_path: this.watcherForm.watch_folder_path,
            notification_emails: this.watcherForm.notification_emails,
            file_extensions: this.watcherForm.file_extensions,
            is_active: this.watcherForm.is_active
          })

          // Auto-preseed existing files so they don't trigger notifications
          // Only new files uploaded AFTER this point will send notifications
          if (createResponse.watcher && createResponse.watcher.id) {
            try {
              const preSeedResult = await S3UploadsApi.preSeedWatcher(createResponse.watcher.id)
              const seededCount = preSeedResult.seededCount || 0
              this.$buefy.toast.open({
                message: `Watcher created and ${seededCount} existing files registered (won't trigger notifications)`,
                type: 'is-success',
                duration: 4000
              })
            } catch (preSeedError) {
              console.warn('Pre-seed failed, existing files may trigger notifications:', preSeedError)
              this.$buefy.toast.open({
                message: 'Watcher created (warning: existing files may trigger notifications)',
                type: 'is-warning',
                duration: 4000
              })
            }
          } else {
            this.$buefy.toast.open({
              message: 'Watcher created successfully',
              type: 'is-success'
            })
          }
        }
        this.closeWatcherModal()
        await this.loadS3Watchers()
      } catch (error) {
        console.error('Failed to save watcher:', error)
        this.$buefy.toast.open({
          message: 'Failed to save watcher: ' + (error.response?.data?.error || error.message),
          type: 'is-danger',
          duration: 5000
        })
      } finally {
        this.savingWatcher = false
      }
    },

    confirmDeleteWatcher(watcher) {
      this.$buefy.dialog.confirm({
        title: 'Delete Watcher',
        message: `Are you sure you want to delete the watcher for <b>${this.getStorageConfigName(watcher.storage_config_id)}</b>? This will stop monitoring the folder but will not delete any detected files.`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deleteWatcher(watcher.id)
      })
    },

    async deleteWatcher(watcherId) {
      try {
        await S3UploadsApi.deleteWatcher(watcherId)
        this.$buefy.toast.open({
          message: 'Watcher deleted successfully',
          type: 'is-success'
        })
        await this.loadS3Watchers()
      } catch (error) {
        console.error('Failed to delete watcher:', error)
        this.$buefy.toast.open({
          message: 'Failed to delete watcher: ' + (error.response?.data?.error || error.message),
          type: 'is-danger'
        })
      }
    },

    async preSeedWatcher(watcherId) {
      this.$buefy.dialog.confirm({
        title: 'Pre-seed Existing Files',
        message: 'This will mark all existing files in the watched folder as "already detected" so they won\'t trigger notifications. Use this when setting up a new watcher on an existing folder. Continue?',
        confirmText: 'Pre-seed',
        type: 'is-warning',
        hasIcon: true,
        onConfirm: async () => {
          try {
            const response = await S3UploadsApi.preSeedWatcher(watcherId)
            const seededCount = response.data?.seeded_count || 0
            this.$buefy.toast.open({
              message: `Pre-seeded ${seededCount} existing files`,
              type: 'is-success'
            })
            await this.loadS3Detections()
          } catch (error) {
            console.error('Failed to pre-seed watcher:', error)
            this.$buefy.toast.open({
              message: 'Failed to pre-seed: ' + (error.response?.data?.error || error.message),
              type: 'is-danger'
            })
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.has-margin-top-15 {
  margin-top: 15px;
}

.has-margin-top-30 {
  margin-top: 30px;
}

.ml-2 {
  margin-left: 0.5rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.mr-1 {
  margin-right: 0.25rem;
}

.mb-3 {
  margin-bottom: 0.75rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.has-margin-bottom-15 {
  margin-bottom: 15px;
}

.has-margin-top-10 {
  margin-top: 10px;
}

.iam-policy-code,
.setup-guide-code {
  background-color: #2d2d2d;
  color: #f8f8f2;
  padding: 15px;
  border-radius: 6px;
  font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
  font-size: 12px;
  overflow-x: auto;
  white-space: pre;
  line-height: 1.5;
}
</style>
